<template>
  <div :style="styleObject">
    <button
      style="display: none"
      type="button"
      class="btn-show-item-detail-modal"
      data-bs-toggle="modal"
      data-bs-target="#modal_item_detail"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-item-detail-modal"
      data-bs-target="#modal_item_detail"
      data-bs-dismiss="modal"
    ></button>
    <div id="custom-overlay" class="custom-modal-overlay"></div>
    <!-- KSK021 — full-screen customize sheet, covering the kiosk header too. -->
    <div
      class="modal modal-full fade ksk-sheet"
      id="modal_item_detail"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modal_item_detail"
      style="z-index: 10000"
      data-bs-backdrop="false"
    >
      <!-- No backdrop: the sheet covers the whole screen, and Bootstrap would
           otherwise wait for the backdrop fade before starting the sheet. -->
      <div
        class="modal-dialog"
        :class="{ 'is-closing': sheetClosing }"
        role="document"
      >
        <div class="modal-content">
          <!-- Sticky title bar once the hero has scrolled away -->
          <div class="ksk-sheet__title-bar" :class="{ 'is-visible': showStickyTitle }">
            <span class="ksk-sheet__title-name">{{ tName(itemInfo.item_name) }}</span>
            <span class="ksk-sheet__title-price">${{ formatMoney(totalPrice) }}</span>
            <button
              type="button"
              class="ksk-sheet__close ksk-sheet__close--bar"
              :aria-label="$t('kiosk.common.close')"
              @click="closeModel()"
            >
              <span class="material-icons-round">close</span>
            </button>
          </div>

          <div
            ref="sheetBody"
            class="modal-body ksk-sheet__body"
            v-on:scroll.passive="handleScroll($event)"
          >
            <!-- KSK063: the sheet opens on tap, before getItemDetail answers;
                 this placeholder holds the layout until the data arrives. -->
            <div v-if="loading" class="ksk-sheet__content ksk-skel" aria-busy="true">
              <div class="ksk-hero ksk-skel__hero">
                <button
                  type="button"
                  class="ksk-sheet__close ksk-sheet__close--hero"
                  :aria-label="$t('kiosk.common.close')"
                  @click="closeModel()"
                >
                  <span class="material-icons-round">close</span>
                </button>
              </div>
              <div class="ksk-skel__card">
                <div class="ksk-skel__line ksk-skel__line--title"></div>
                <div class="ksk-skel__line ksk-skel__line--price"></div>
                <div class="ksk-skel__line"></div>
                <div class="ksk-skel__line ksk-skel__line--short"></div>
              </div>
              <div class="ksk-skel__card ksk-skel__card--rows">
                <div class="ksk-skel__line ksk-skel__line--row"></div>
                <div class="ksk-skel__line ksk-skel__line--row"></div>
                <div class="ksk-skel__line ksk-skel__line--row"></div>
              </div>
            </div>
            <div class="ksk-sheet__content" v-else-if="isLoadDone">
              <!-- Hero -->
              <div ref="hero" class="ksk-hero">
                <!-- Item photos are uploaded square: shown whole (not cropped to
                     the wide hero), over a blurred copy that fills the sides.
                     Keyed by URL so each item gets fresh <img>s that fade in once
                     loaded, instead of the old photo / a blank flashing. -->
                <img
                  :key="'bg-' + itemInfo.image"
                  :src="itemInfo.image"
                  alt=""
                  aria-hidden="true"
                  class="ksk-hero__backdrop"
                  :class="{ 'is-loaded': heroLoaded }"
                />
                <div class="ksk-hero__frame">
                  <img
                    :key="itemInfo.image"
                    :src="itemInfo.image"
                    alt=""
                    class="food-image"
                    :class="{ 'is-loaded': heroLoaded }"
                    @load="heroLoaded = true"
                    @error="heroLoaded = true"
                  />
                </div>
                <button
                  type="button"
                  class="ksk-sheet__close ksk-sheet__close--hero"
                  :aria-label="$t('kiosk.common.close')"
                  @click="closeModel()"
                >
                  <span class="material-icons-round">close</span>
                </button>
              </div>

              <!-- Name / price / description -->
              <section class="ksk-card ksk-info">
                <div class="ksk-info__top">
                  <h1 class="ksk-info__name">{{ tName(itemInfo.item_name) }}</h1>
                  <div class="ksk-info__prices">
                    <!-- Struck-through price on the same row, before the price paid. -->
                    <div class="ksk-info__price-row">
                      <span v-if="showRegularPrice" class="ksk-info__regular">
                        ${{ formatMoney(regularUnitPrice * (itemInfo.quantity || 1)) }}
                      </span>
                      <!-- KSK119: Special Scheduled Price - the usual price struck through. -->
                      <span v-else-if="scheduleRegularTotal > totalPrice" class="ksk-info__regular">
                        ${{ formatMoney(scheduleRegularTotal) }}
                      </span>
                      <span class="ksk-info__price">${{ formatMoney(totalPrice) }}</span>
                    </div>
                    <span v-if="showRegularPrice" class="ksk-info__member">
                      <span class="material-icons-round">loyalty</span>
                      {{ $t("kiosk.itemDetail.memberPrice") }}
                    </span>
                    <!-- KSK119: the Special Scheduled Price period. -->
                    <span v-else-if="scheduleRegularTotal > totalPrice && specialPriceText" class="ksk-info__member">
                      <span class="material-icons-round">schedule</span>
                      {{ specialPriceText }}
                    </span>
                  </div>
                </div>
                <template v-if="branch && branch.show_mog_description_on_kiosk && itemInfo.description">
                  <p
                    ref="description"
                    class="ksk-info__desc"
                    :class="{ 'is-clamped': !descriptionExpanded }"
                  >{{ itemInfo.description }}</p>
                  <button
                    v-if="descriptionOverflows"
                    type="button"
                    class="ksk-info__more"
                    @click="descriptionExpanded = !descriptionExpanded"
                  >
                    {{ $t(descriptionExpanded ? "kiosk.itemDetail.less" : "kiosk.itemDetail.more") }}
                  </button>
                </template>

                <ul v-if="itemInfo.bundle_item.length" class="ksk-info__bundle">
                  <li v-for="(bundleItem, index) in itemInfo.bundle_item" :key="index">
                    <div>
                      <strong>{{ tName(bundleItem.itemName) }}</strong>
                      <span
                        v-if="branch && branch.show_mog_description_on_kiosk && bundleItem.description"
                        class="ksk-info__bundle-desc"
                      >{{ bundleItem.description }}</span>
                    </div>
                    <span class="ksk-info__bundle-qty">x {{ bundleItem.quantity }}</span>
                  </li>
                </ul>
              </section>

              <!-- Add-on carousel -->
              <div class="banner ksk-addons" v-if="itemInfo.slides.length">
                <swiper
                  :slides-per-view="1"
                  :space-between="0"
                  :loop="true"
                  :autoplay="{
                    delay: 3000,
                    disableOnInteraction: false,
                  }"
                  :pagination="{
                    clickable: true,
                    el: '.swiper-pagination',
                  }"
                >
                  <swiper-slide
                    v-for="(value, index) in itemInfo.slides"
                    :key="index"
                  >
                    <div class="slider ksk-addons__slide" @click="clickLink(value)">
                      <img :src="value.file_url" alt="" class="ksk-addons__bg" />
                      <div
                        v-if="value.direct_to_suggested_adds_on == 1 && value.itemSelected"
                        class="ksk-addons__card"
                      >
                        <img
                          v-if="value.itemSelected.image && !value.itemSelected.image.includes('noimage.png')"
                          :src="value.itemSelected.image"
                          alt=""
                        />
                        <div class="ksk-addons__text">
                          <span class="ksk-addons__tag">{{ $t("kiosk.itemDetail.addOnPrice", { price: "$" + formatMoney(value.itemSelected.price) }) }}</span>
                          <span class="ksk-addons__name">{{ tName(value.itemSelected.item_name) }}</span>
                        </div>
                      </div>
                    </div>
                  </swiper-slide>
                </swiper>
                <div class="swiper-pagination"></div>
              </div>

              <template v-if="itemInfo.choice">
                <!-- Set-meal choices -->
                <div v-if="!itemInfo.is_sub_category" class="choice-list ksk-tabs">
                  <button
                    v-for="(choice, index) in itemInfo.choices"
                    :key="index"
                    type="button"
                    :class="['choice-tab', 'choice-' + index, { active: index == selectedChoiceIndex }]"
                    @click="selectChoice(index)"
                  >
                    {{ tName(choice.name) }}
                  </button>
                </div>

                <!-- Sub-category: choose ONE item; its modifiers open in a modal -->
                <section v-if="itemInfo.is_sub_category" class="ksk-card ksk-subcat">
                  <div
                    v-for="(item, indexChoice) in choiceSelected.items"
                    :key="indexChoice"
                    v-show="item.item_detail"
                    class="ksk-subcat__row"
                    :class="{
                      'is-selected': selectedSubCategoryIndex === indexChoice,
                      'is-sold-out': !isSubCategoryItemAvailable(item),
                    }"
                    @click="selectSubCategoryItem(indexChoice)"
                  >
                    <template v-if="item.item_detail">
                      <div class="ksk-subcat__main">
                        <span class="ksk-subcat__radio"></span>
                        <div class="ksk-subcat__body">
                          <span class="ksk-subcat__name">{{ item.qty + " x " + tName(item.item_detail.item_name) }}</span>
                          <span
                            v-if="branch && branch.show_mog_description_on_kiosk && item.item_detail.description"
                            class="ksk-subcat__desc"
                          >{{ item.item_detail.description }}</span>
                          <span v-if="!isSubCategoryItemAvailable(item)" class="ksk-subcat__sold-out">{{ $t("kiosk.common.soldOut") }}</span>
                        </div>
                        <span class="ksk-subcat__price">
                          ${{
                            isLogined
                              ? parseFloat(
                                  item.item_detail.member_price ||
                                    item.item_detail.price_default ||
                                    0
                                ).toFixed(2)
                              : parseFloat(
                                  item.item_detail.price_default || 0
                                ).toFixed(2)
                          }}
                        </span>
                      </div>
                      <!-- Preview of selected modifiers (no need to reopen modal) -->
                      <div
                        v-if="selectedSubCategoryIndex === indexChoice && selectedModifierList(item.item_detail).length"
                        class="ksk-subcat__summary"
                      >
                        <div
                          v-for="(mod, mi) in selectedModifierList(item.item_detail)"
                          :key="mi"
                          class="ksk-subcat__mod"
                          :class="{ nested: mod.nested }"
                        >
                          <span>{{ mod.quantity }} x {{ tName(mod.name) }}</span>
                          <span v-if="mod.price > 0" class="ksk-subcat__mod-price">
                            +${{ (mod.price * mod.quantity).toFixed(2) }}
                          </span>
                        </div>
                      </div>
                    </template>
                  </div>
                </section>

                <!-- Normal choice: inline modifiers for every item -->
                <template v-else>
                  <div
                    v-for="(item, indexChoice) in choiceSelected.items"
                    :key="indexChoice"
                    class="ksk-choice-item"
                  >
                    <template v-if="item.item_detail">
                      <div class="ksk-choice-item__head">
                        <span class="ksk-choice-item__name">{{ item.qty + " x " + tName(item.item_detail.item_name) }}</span>
                        <span
                          v-if="branch && branch.show_mog_description_on_kiosk && item.item_detail.description"
                          class="ksk-choice-item__desc"
                        >{{ item.item_detail.description }}</span>
                      </div>
                      <item-modifier
                        :keyReload="itemModifierKey"
                        :itemInfo="item.item_detail"
                        :selectOnlyItem="selectOnlyItemChoice"
                        :selectMultiItem="selectMultiItemChoice"
                        :removeModifierOptionGroup="
                          removeModifierOptionGroupChoice
                        "
                        :addModifierOptionGroup="
                          addModifierOptionGroupChoice
                        "
                        :indexChoice="indexChoice"
                        :isLoadingModifiers="isLoadingModifiers"
                        :onBeforeItemClick="onBeforeItemClick"
                        :onAfterItemClick="onAfterItemClick"
                        :onShowMoreModifier="openShowMoreModifier"
                        :highlightUnmet="highlightUnmet"
                      >
                      </item-modifier>
                      <div v-if="isLoadingModifiers" class="modifier-loading-container">
                        <div class="spinner-border" role="status"></div>
                        <div class="loading-text">{{ $t("kiosk.itemDetail.loadingOptions") }}</div>
                      </div>
                    </template>
                  </div>
                </template>
              </template>
              <template v-else>
                <item-modifier
                  :keyReload="itemModifierKey"
                  :itemInfo="itemInfo"
                  :selectOnlyItem="selectOnlyItem"
                  :selectMultiItem="selectMultiItem"
                  :removeModifierOptionGroup="removeModifierOptionGroup"
                  :addModifierOptionGroup="addModifierOptionGroup"
                  :isLoadingModifiers="isLoadingModifiers"
                  :onBeforeItemClick="onBeforeItemClick"
                  :onAfterItemClick="onAfterItemClick"
                  :onShowMoreModifier="openShowMoreModifier"
                  :highlightUnmet="highlightUnmet"
                >
                </item-modifier>
                <div v-if="isLoadingModifiers" class="modifier-loading-container">
                  <div class="spinner-border" role="status"></div>
                  <div class="loading-text">{{ $t("kiosk.itemDetail.loadingOptions") }}</div>
                </div>
              </template>
            </div>
            <material-loading :active="loaderActive" />
          </div>

          <!-- Unmet required groups -->
          <transition name="ksk-fade">
            <div v-if="unmetMessage" class="ksk-sheet__unmet" role="alert">
              <span class="material-icons-round">error</span>
              {{ unmetMessage }}
            </div>
          </transition>

          <!-- Bottom bar -->
          <div v-if="isLoadDone && !loading" class="add-to-cart ksk-sheet__bar">
            <KskStepper
              :modelValue="itemInfo.quantity"
              :min="1"
              @decrement="removeItem()"
              @increment="addItem()"
            />
            <KskButton
              class="btn-add-to-cart"
              :class="{ 'is-incomplete': itemInfo.disable_add_to_cart }"
              icon="shopping_bag"
              size="md"
              block
              @click="addToCart()"
            >
              {{ $t("kiosk.itemDetail.addToCart", { price: "$" + formatMoney(totalPrice) }) }}
            </KskButton>
          </div>
        </div>
      </div>
      <!-- KSK062: the sheet covers App.vue's strip, so it carries its own.
           KSK108: kept outside .modal-dialog so the open scale doesn't move
           it — it sits exactly over App.vue's strip and only fades. -->
      <KskPoweredBy />
    </div>
  </div>

  <ModalNotification :message="messError" :isKiosk="true"></ModalNotification>

  <ModalItemModifierOption
    :show="showModifierOptionModal"
    :item="modifierOptionItem"
    :indexChoice="modifierOptionContext ? modifierOptionContext.indexChoice : -1"
    :index="modifierOptionContext ? modifierOptionContext.index : -1"
    :index2="modifierOptionContext ? modifierOptionContext.index2 : -1"
    :selectOnlyItem="itemInfo.choice ? selectOnlyItemChoice : selectOnlyItem"
    :selectMultiItem="itemInfo.choice ? selectMultiItemChoice : selectMultiItem"
    :addModifierOptionGroup="itemInfo.choice ? addModifierOptionGroupChoice : addModifierOptionGroup"
    :removeModifierOptionGroup="itemInfo.choice ? removeModifierOptionGroupChoice : removeModifierOptionGroup"
    @confirm="confirmModifierOption"
    @cancel="cancelModifierOption"
  />

  <ModalSubCategoryModifier
    kiosk
    :show="showSubCategoryModal"
    :item="selectedSubCategoryItemDetail || {}"
    @confirm="confirmSubCategoryModal"
    @cancel="cancelSubCategoryModal"
  >
    <div class="subcat-modal-body">
      <item-modifier
        v-if="selectedSubCategoryItemDetail"
        :keyReload="itemModifierKey"
        :itemInfo="selectedSubCategoryItemDetail"
        :selectOnlyItem="selectOnlyItemChoice"
        :selectMultiItem="selectMultiItemChoice"
        :removeModifierOptionGroup="removeModifierOptionGroupChoice"
        :addModifierOptionGroup="addModifierOptionGroupChoice"
        :indexChoice="selectedSubCategoryIndex"
        :isLoadingModifiers="isLoadingModifiers"
        :onBeforeItemClick="onBeforeItemClick"
        :onAfterItemClick="onAfterItemClick"
        :onShowMoreModifier="openShowMoreModifier"
      >
      </item-modifier>
    </div>
  </ModalSubCategoryModifier>
</template>

<script>
import OrderService from "../../../services/order.service";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalNotification from "@/components/ModalNotification.vue";
import $ from "jquery";
import EventBus from "vue3-eventbus";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Pagination, Autoplay } from "swiper";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import { localStorageName, getDiningOption } from "@/config/utils";
import { KIOSK_WEIGH_MIN_ADD_CART } from "@/config/const";
import "bootstrap/dist/js/bootstrap.bundle";
import ItemModifier from "../../components/KioskItemModifierNew.vue";
import ModalItemModifierOption from "./ModalItemModifierOption.vue";
import ModalSubCategoryModifier from "@/components/ModalSubCategoryModifier.vue";
import KskStepper from "./ui/KskStepper.vue";
import KskButton from "./ui/KskButton.vue";
import KskPoweredBy from "./ui/KskPoweredBy.vue";
import { collectNamesDeep, syncMenuTranslations, translatedName } from "@/i18n/menuTranslate";
import { scheduleOriginalPrice, specialPriceLabel } from "@/config/specialPrice";

SwiperCore.use([Pagination, Autoplay]);

export default {
  name: "Item Detail",
  components: {
    KskStepper,
    KskButton,
    KskPoweredBy,
    MaterialLoading,
    Swiper,
    SwiperSlide,
    ModalNotification,
    ItemModifier,
    ModalItemModifierOption,
    ModalSubCategoryModifier,
  },
  props: {
    categoryId: {
      type: Number,
      required: true,
    },
    itemId: {
      type: Number,
      required: true,
    },
    itemDetail: {
      type: Object,
      required: true,
    },
    isLogined: {
      type: Boolean,
      default: false,
    },
    reloadModal: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editItemIndex: {
      type: Number,
      default: -1,
    },
    // KSK063: true while the parent waits for getItemDetail (sheet already open).
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoadDone: false,
      loaderActive: false,
      merchantCode: "",
      branchCode: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      itemInfo: {},
      disableBtnAddToCart: false,
      getModifierToShowSlideSelected: [],
      cartName: "ORDER_INFO",
      reloadModal1: "reload-item-detail",
      originalChoices: [],
      choiceSelected: {},
      selectedChoiceIndex: 0,
      itemModifierKey: "itemModifierKey",
      isLoadingModifiers: false, // Add loading state for modifiers
      showModifierOptionModal: false,
      modifierOptionContext: null,
      modifierOptionSnapshot: null,
      // Sub-category single-select state
      selectedSubCategoryIndex: -1,
      showSubCategoryModal: false,
      subCategoryModalSnapshot: null,
      // KSK021 sheet UI state
      showStickyTitle: false,
      heroLoaded: false,
      descriptionExpanded: false,
      descriptionOverflows: false,
      highlightUnmet: false,
      unmetMessage: "",
      unmetTimer: null,
      // KSK071: light exit — see closeModel() / onSheetHide()
      sheetClosing: false,
      clearDataOnHidden: false,
    };
  },
  mounted() {
    // Tell the kiosk header to show the "Customize" step while the sheet is open.
    const modal = document.getElementById("modal_item_detail");
    if (modal) {
      modal.addEventListener("show.bs.modal", this.onSheetShow);
      modal.addEventListener("hide.bs.modal", this.onSheetHide);
      modal.addEventListener("hidden.bs.modal", this.onSheetHidden);
    }
  },
  async created() {
    this.cartName = await this.$store.dispatch("cartName");
  },
  beforeUnmount() {
    window.removeEventListener("popstate", this.closeModel(true));
    const modal = document.getElementById("modal_item_detail");
    if (modal) {
      modal.removeEventListener("show.bs.modal", this.onSheetShow);
      modal.removeEventListener("hide.bs.modal", this.onSheetHide);
      modal.removeEventListener("hidden.bs.modal", this.onSheetHidden);
    }
    clearTimeout(this.unmetTimer);
    EventBus.emit("KioskCustomizeSheet", false);
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },
    // Unit price incl. selected modifiers — the price that goes into the cart.
    unitPrice() {
      const price = this.isLogined
        ? this.itemInfo.member_price
        : this.itemInfo.price_default;
      return parseFloat(price) || 0;
    },
    regularUnitPrice() {
      return parseFloat(this.itemInfo.price_default) || 0;
    },
    // Logged-in member paying less than the regular price.
    showRegularPrice() {
      return this.isLogined && this.regularUnitPrice > this.unitPrice;
    },
    totalPrice() {
      return this.unitPrice * (this.itemInfo.quantity || 1);
    },
    // KSK119: the total at the usual base price (same modifiers / quantity); 0 off schedule.
    // A member's usual price is their member price.
    scheduleRegularTotal() {
      const original = scheduleOriginalPrice(this.itemInfo, this.isLogined);
      if (!original) return 0;
      const base = parseFloat(this.isLogined
        ? this.itemInfo.member_price_no_modifier_option
        : this.itemInfo.price_default_no_modifier_option) || 0;
      return (this.unitPrice + original - base) * (this.itemInfo.quantity || 1);
    },
    specialPriceText() {
      return specialPriceLabel(this.itemInfo, this.$t, this.isLogined);
    },
    modifierOptionItem() {
      const ctx = this.modifierOptionContext;
      if (!ctx) return {};
      const itemSelect = this.itemInfo.choice
        ? this.choiceSelected &&
          this.choiceSelected.items &&
          this.choiceSelected.items[ctx.indexChoice] &&
          this.choiceSelected.items[ctx.indexChoice].item_detail
        : this.itemInfo;
      if (!itemSelect || !itemSelect.modifier_group) return {};
      const group = itemSelect.modifier_group[ctx.index];
      if (!group || !group.items) return {};
      return group.items[ctx.index2] || {};
    },
    // item_detail of the currently selected sub-category item (for the modal).
    selectedSubCategoryItemDetail() {
      if (!this.itemInfo.is_sub_category) return null;
      const idx = this.selectedSubCategoryIndex;
      const items = this.choiceSelected && this.choiceSelected.items;
      if (idx < 0 || !items || !items[idx]) return null;
      return items[idx].item_detail || null;
    },
  },
  methods: {
    // KSK054: menu names in the language picked on the kiosk Landing.
    tName(source) {
      return translatedName(source);
    },

    formatPrice(price) {
      return "$ " + parseFloat(price).toFixed(2);
    },

    closeModel(isPopState = false) {
      if (!isPopState) {
        // KSK071: clearing the item re-renders the whole menu behind the
        // sheet; wait until the exit fade has finished (onSheetHidden).
        const modal = document.getElementById("modal_item_detail");
        if (modal && modal.classList.contains("show")) {
          this.clearDataOnHidden = true;
        } else {
          this.$emit("clear-data-item");
        }
      }

      $(".btn-hide-item-detail-modal").click();
    },

    formatMoney(value) {
      return (parseFloat(value) || 0).toFixed(2);
    },

    handleScroll(event) {
      const hero = this.$refs.hero;
      const heroBottom = hero ? hero.offsetTop + hero.offsetHeight : 0;
      this.showStickyTitle = event.target.scrollTop >= heroBottom - 80;
    },

    onSheetShow() {
      EventBus.emit("KioskCustomizeSheet", true);
    },

    onSheetHide() {
      this.sheetClosing = true;
    },

    onSheetHidden() {
      this.sheetClosing = false;
      if (this.clearDataOnHidden) {
        this.clearDataOnHidden = false;
        this.$emit("clear-data-item");
      }
      EventBus.emit("KioskCustomizeSheet", false);
    },

    resetSheetUi() {
      this.showStickyTitle = false;
      this.descriptionExpanded = false;
      this.descriptionOverflows = false;
      this.highlightUnmet = false;
      this.unmetMessage = "";
      clearTimeout(this.unmetTimer);
      if (this.$refs.sheetBody) this.$refs.sheetBody.scrollTop = 0;
      this.$nextTick(() => {
        const desc = this.$refs.description;
        this.descriptionOverflows = !!desc && desc.scrollHeight > desc.clientHeight + 1;
      });
    },

    // Required groups still short of min_qty, with the key of their card
    // (data-ksk-group in KioskItemModifierNew). Relies on total_selected having
    // been synced by checkAvailableAddToCart().
    unmetRequiredGroups() {
      const unmet = [];
      const collect = (groups, indexChoice) => {
        (groups || []).forEach((group, index) => {
          if (group.min_qty > (group.total_selected || 0)) {
            unmet.push({ name: group.name, key: `${indexChoice}-${index}` });
          }
        });
      };

      if (this.itemInfo.is_sub_category) {
        const sel = this.selectedSubCategoryItemDetail;
        collect(sel && sel.modifier_group, this.selectedSubCategoryIndex);
      } else if (this.itemInfo.choice) {
        ((this.choiceSelected && this.choiceSelected.items) || []).forEach((item, i) => {
          if (item.item_detail) collect(item.item_detail.modifier_group, i);
        });
      } else {
        collect(this.itemInfo.modifier_group, -1);
      }
      return unmet;
    },

    // Failed Add to Cart: name the missing groups, mark them red and scroll to
    // the first one (for a sub-category item, to its row — its groups live in
    // the sub-category modal).
    showUnmetRequired() {
      const unmet = this.unmetRequiredGroups();
      const names = [...new Set(unmet.map((g) => g.name).filter(Boolean))];
      this.unmetMessage = names.length
        ? this.$t("kiosk.itemDetail.pleaseChoose", { names: names.map(translatedName).join(", ") })
        : this.$t("kiosk.itemDetail.completeSelection");
      this.highlightUnmet = true;
      clearTimeout(this.unmetTimer);
      this.unmetTimer = setTimeout(() => {
        this.unmetMessage = "";
      }, 4000);

      this.$nextTick(() => {
        const body = this.$refs.sheetBody;
        if (!body) return;
        const target = this.itemInfo.is_sub_category
          ? body.querySelector(".ksk-subcat__row.is-selected") ||
            body.querySelector(".ksk-subcat")
          : unmet.length
          ? body.querySelector(`[data-ksk-group="${unmet[0].key}"]`)
          : null;
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },

    showHideModifier(index) {
      if (this.itemInfo.modifier_group[index]) {
        if (this.itemInfo.modifier_group[index].is_show) {
          this.itemInfo.modifier_group[index].is_show = false;
        } else {
          this.itemInfo.modifier_group.forEach((item, idx) => {
            this.itemInfo.modifier_group[idx].is_show = false;
          });

          this.itemInfo.modifier_group[index].is_show = true;
        }
      }
    },

    selectChoice(index) {
      // A different choice has different groups — drop the old unmet notice.
      this.unmetMessage = "";
      this.highlightUnmet = false;
      this.itemModifierKey = Math.random();
      this.reloadModifier = Math.random();

      // Save current choice's modifier state back to originalChoices before switching
      const originalChoices = JSON.parse(this.originalChoices);
      if (this.choiceSelected && this.choiceSelected.items) {
        originalChoices[this.selectedChoiceIndex] = JSON.parse(JSON.stringify(this.choiceSelected));
      }
      this.originalChoices = JSON.stringify(originalChoices);

      // Switch to the new choice
      this.selectedChoiceIndex = index;
      this.choiceSelected = originalChoices[index];

      // Sub-category: single-select behaviour — default to the lowest-price item
      // and derive the price from that item only (not the sum of all items).
      if (this.itemInfo.is_sub_category) {
        this.defaultSelectLowestSubCategory();
        this.checkAvailableAddToCart();
        return;
      }

      $(".choice-list > .choice-tab").removeClass("active");
      $(".choice-list > .choice-" + index).addClass("active");

      this.itemInfo.price_default_no_modifier_option = this.choiceSelected.default_price;
      this.itemInfo.price_default = this.choiceSelected.default_price;
      this.itemInfo.member_price_no_modifier_option = this.choiceSelected.member_price;
      this.itemInfo.member_price = this.choiceSelected.member_price;
      this.applyChoiceSchedule();

      // Recalculate price including any pre-selected modifiers (from cart restore).
      // Auto-default for required groups is intentionally skipped — user must pick.
      this.recalcChoiceModifierPrice();

      this.checkAvailableAddToCart();
    },

    // KSK119 for choices: the picked choice's usual price (from item-detail) is struck through
    // next to its scheduled price, like a Main Item.
    applyChoiceSchedule() {
      this.itemInfo.original_price = this.choiceSelected ? this.choiceSelected.original_price : undefined;
      this.itemInfo.original_member_price = this.choiceSelected ? this.choiceSelected.original_member_price : undefined;
    },

    recalcChoiceModifierPrice() {
      if (!this.choiceSelected || !this.choiceSelected.items) return;

      let totalModifierPrice = 0;
      this.choiceSelected.items.forEach((choiceItem) => {
        if (!choiceItem.item_detail || !choiceItem.item_detail.modifier_group) return;
        const itemChoiceQty = choiceItem.qty || 1;

        choiceItem.item_detail.modifier_group.forEach((group) => {
          group.items.forEach((modifierItem) => {
            if (modifierItem.selected) {
              totalModifierPrice +=
                (modifierItem.price_default || 0) *
                (modifierItem.quantity || 1) *
                itemChoiceQty;

              // Nested modifiers (item_in_mog)
              if (modifierItem.item_in_mog && modifierItem.item_in_mog.modifier_group) {
                modifierItem.item_in_mog.modifier_group.forEach((subGroup) => {
                  subGroup.items.forEach((subItem) => {
                    if (subItem.selected) {
                      totalModifierPrice +=
                        (subItem.price_default || 0) *
                        (subItem.quantity || 1) *
                        (modifierItem.quantity || 1) *
                        itemChoiceQty;
                    }
                  });
                });
              }
            }
          });
        });
      });

      this.itemInfo.price_default += totalModifierPrice;
      this.itemInfo.member_price += totalModifierPrice;
    },

    // Reset price to base then re-sum all currently selected modifiers.
    // Defensive: keeps price in sync with `selected` state regardless of
    // any incremental drift in the per-click ADD/SUBTRACT handlers.
    recalcAllPrices() {
      if (this.itemInfo.is_sub_category) {
        this.recalcSubCategoryPrice();
        return;
      }
      if (this.itemInfo.choice && this.choiceSelected) {
        this.itemInfo.price_default = parseFloat(
          this.choiceSelected.default_price || 0
        );
        this.itemInfo.member_price = parseFloat(
          this.choiceSelected.member_price || 0
        );
        this.recalcChoiceModifierPrice();
      } else if (this.itemInfo.modifier_group) {
        this.itemInfo.price_default = parseFloat(
          this.itemInfo.price_default_no_modifier_option || 0
        );
        this.itemInfo.member_price = parseFloat(
          this.itemInfo.member_price_no_modifier_option || 0
        );
        let totalModifierPrice = 0;
        this.itemInfo.modifier_group.forEach((group) => {
          if (!Array.isArray(group.items)) return;
          group.items.forEach((modifierItem) => {
            if (!modifierItem.selected) return;
            totalModifierPrice +=
              (modifierItem.price_default || 0) *
              (modifierItem.quantity || 1);
            if (
              modifierItem.item_in_mog &&
              modifierItem.item_in_mog.modifier_group
            ) {
              modifierItem.item_in_mog.modifier_group.forEach((subGroup) => {
                if (!Array.isArray(subGroup.items)) return;
                subGroup.items.forEach((subItem) => {
                  if (!subItem.selected) return;
                  totalModifierPrice +=
                    (subItem.price_default || 0) *
                    (subItem.quantity || 1) *
                    (modifierItem.quantity || 1);
                });
              });
            }
          });
        });
        this.itemInfo.price_default += totalModifierPrice;
        this.itemInfo.member_price += totalModifierPrice;
      }
    },

    // ---- Sub-category single-select helpers ----
    isSubCategoryItemAvailable(choiceItem) {
      const d = choiceItem && choiceItem.item_detail;
      if (!d) return false;
      if (d.stock_enable_balance) return d.stock_balance > 0;
      return !d.is_not_available;
    },

    // Flatten the selected modifiers (incl. nested) of a sub-category item so we
    // can preview them under the selected item without opening the modal.
    selectedModifierList(itemDetail) {
      const result = [];
      if (!itemDetail || !Array.isArray(itemDetail.modifier_group)) return result;
      itemDetail.modifier_group.forEach((group) => {
        if (!Array.isArray(group.items)) return;
        group.items.forEach((it) => {
          if (!it.selected) return;
          result.push({
            name: it.item_name,
            quantity: it.quantity || 1,
            price: it.price_default || 0,
            nested: false,
          });
          if (it.item_in_mog && Array.isArray(it.item_in_mog.modifier_group)) {
            it.item_in_mog.modifier_group.forEach((sg) => {
              if (!Array.isArray(sg.items)) return;
              sg.items.forEach((si) => {
                if (!si.selected) return;
                result.push({
                  name: si.item_name,
                  quantity: si.quantity || 1,
                  price: si.price_default || 0,
                  nested: true,
                });
              });
            });
          }
        });
      });
      return result;
    },

    // Recompute itemInfo price from the currently selected sub-category item only
    // (its base price for the current login state + its selected modifiers).
    recalcSubCategoryPrice() {
      const idx = this.selectedSubCategoryIndex;
      const items = this.choiceSelected && this.choiceSelected.items;

      if (idx < 0 || !items || !items[idx] || !items[idx].item_detail) {
        this.itemInfo.price_default_no_modifier_option = 0;
        this.itemInfo.member_price_no_modifier_option = 0;
        this.itemInfo.price_default = 0;
        this.itemInfo.member_price = 0;
        return;
      }

      const choiceItem = items[idx];
      const itemDetail = choiceItem.item_detail;
      const qty = choiceItem.qty || 1;

      const baseDefault = parseFloat(itemDetail.price_default || 0);
      const baseMember = parseFloat(
        itemDetail.member_price || itemDetail.price_default || 0
      );

      this.itemInfo.price_default_no_modifier_option = baseDefault * qty;
      this.itemInfo.member_price_no_modifier_option = baseMember * qty;
      // Keep choiceSelected base in sync for downstream (compareObjects / cart).
      this.choiceSelected.default_price = baseDefault * qty;
      this.choiceSelected.member_price = baseMember * qty;

      let mods = 0;
      if (Array.isArray(itemDetail.modifier_group)) {
        itemDetail.modifier_group.forEach((group) => {
          if (!Array.isArray(group.items)) return;
          group.items.forEach((mItem) => {
            if (!mItem.selected) return;
            mods += (mItem.price_default || 0) * (mItem.quantity || 1) * qty;
            if (mItem.item_in_mog && Array.isArray(mItem.item_in_mog.modifier_group)) {
              mItem.item_in_mog.modifier_group.forEach((sg) => {
                if (!Array.isArray(sg.items)) return;
                sg.items.forEach((sItem) => {
                  if (!sItem.selected) return;
                  mods +=
                    (sItem.price_default || 0) *
                    (sItem.quantity || 1) *
                    (mItem.quantity || 1) *
                    qty;
                });
              });
            }
          });
        });
      }

      this.itemInfo.price_default =
        this.itemInfo.price_default_no_modifier_option + mods;
      this.itemInfo.member_price =
        this.itemInfo.member_price_no_modifier_option + mods;
    },

    // Default-select the lowest-price available sub-category item (no modal).
    defaultSelectLowestSubCategory() {
      const items = this.choiceSelected && this.choiceSelected.items;
      if (!Array.isArray(items) || !items.length) return;

      let lowestIdx = -1;
      let lowestPrice = Infinity;
      items.forEach((it, i) => {
        if (!it.item_detail || !this.isSubCategoryItemAvailable(it)) return;
        const p = this.isLogined
          ? parseFloat(it.item_detail.member_price || it.item_detail.price_default || 0)
          : parseFloat(it.item_detail.price_default || 0);
        if (p < lowestPrice) {
          lowestPrice = p;
          lowestIdx = i;
        }
      });
      if (lowestIdx === -1) lowestIdx = 0;

      this.selectedSubCategoryIndex = lowestIdx;
      this.recalcSubCategoryPrice();
    },

    // User picks a sub-category item: select it and open its modifiers modal (if any).
    selectSubCategoryItem(index) {
      const items = this.choiceSelected && this.choiceSelected.items;
      if (!items || !items[index] || !items[index].item_detail) return;
      if (!this.isSubCategoryItemAvailable(items[index])) return;

      this.selectedSubCategoryIndex = index;
      this.itemModifierKey = Math.random();
      this.recalcSubCategoryPrice();
      this.checkAvailableAddToCart();

      const itemDetail = items[index].item_detail;
      const hasModifiers =
        Array.isArray(itemDetail.modifier_group) &&
        itemDetail.modifier_group.length > 0;

      if (hasModifiers) {
        // Snapshot so Cancel reverts modifier changes made inside the modal.
        this.subCategoryModalSnapshot = {
          itemInfo: JSON.parse(JSON.stringify(this.itemInfo)),
          choiceSelected: JSON.parse(JSON.stringify(this.choiceSelected)),
          selectedSubCategoryIndex: index,
        };
        this.showSubCategoryModal = true;
      }
    },

    confirmSubCategoryModal() {
      this.showSubCategoryModal = false;
      this.subCategoryModalSnapshot = null;
      this.checkAvailableAddToCart();
    },

    cancelSubCategoryModal() {
      if (this.subCategoryModalSnapshot) {
        this.itemInfo = this.subCategoryModalSnapshot.itemInfo;
        this.choiceSelected = this.subCategoryModalSnapshot.choiceSelected;
        this.selectedSubCategoryIndex =
          this.subCategoryModalSnapshot.selectedSubCategoryIndex;
        this.subCategoryModalSnapshot = null;
        this.itemModifierKey = Math.random();
      }
      this.showSubCategoryModal = false;
      this.checkAvailableAddToCart();
    },

    addModifierOptionGroup(
      indexChoice = -1,
      index,
      index2,
      index3 = -1,
      index4 = -1
    ) {
      console.log(indexChoice);
      let modifierGroup = this.itemInfo.modifier_group[index];
      let modifierGroupItem = this.itemInfo.modifier_group[index].items[index2];
      // let parentModifierGroupItem = this.itemInfo.modifier_group[index].items[index2];
      let parentTotalSelectd = 1;

      if (index3 != -1) {
        parentTotalSelectd =
          this.itemInfo.modifier_group[index].items[index2].quantity ?? 1;;
        modifierGroup = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3];
        modifierGroupItem = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (
          modifierGroupItem.quantity < modifierGroupItem.item_max_qty &&
          modifierGroup.total_selected < modifierGroup.max_qty
        ) {
          modifierGroupItem.quantity += 1;
          this.itemInfo.member_price +=
            modifierGroupItem.price_default * parentTotalSelectd;
          this.itemInfo.price_default +=
            modifierGroupItem.price_default * parentTotalSelectd;
          modifierGroup.total_selected += 1;

          // if (index3 != - 1) {
          //   parentModifierGroupItem.price_default += modifierGroupItem.price_default;
          // }

          this.checkAvailableAddToCart();
        }
      }
    },

    addModifierOptionGroupChoice(
      indexChoice = -1,
      index,
      index2,
      index3 = -1,
      index4 = -1
    ) {
      let itemSelect = this.choiceSelected.items[indexChoice].item_detail;
      const itemChoiceQty = this.choiceSelected.items[indexChoice].qty;

      let modifierGroup = itemSelect.modifier_group[index];
      let modifierGroupItem = itemSelect.modifier_group[index].items[index2];
      // let parentModifierGroupItem = itemSelect.modifier_group[index].items[index2];
      let parentTotalSelectd = 1;

      if (index3 != -1) {
        parentTotalSelectd =
          itemSelect.modifier_group[index].items[index2].quantity ?? 1;;
        modifierGroup =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3];
        modifierGroupItem =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (
          modifierGroupItem.quantity < modifierGroupItem.item_max_qty &&
          modifierGroup.total_selected < modifierGroup.max_qty
        ) {
          modifierGroupItem.quantity += 1;
          this.itemInfo.member_price +=
            modifierGroupItem.price_default *
            parentTotalSelectd *
            itemChoiceQty;
          this.itemInfo.price_default +=
            modifierGroupItem.price_default *
            parentTotalSelectd *
            itemChoiceQty;
          modifierGroup.total_selected += 1;

          if (index3 == -1) {
            if (
              modifierGroupItem.item_in_mog &&
              modifierGroupItem.item_in_mog.modifier_group
            ) {
              modifierGroupItem.item_in_mog.modifier_group.forEach(
                (group) => {
                  group.items.forEach((itemModifier) => {
                    if (itemModifier.selected) {
                      this.itemInfo.price_default +=
                        itemModifier.price_default *
                        itemModifier.quantity *
                        itemChoiceQty;
                      this.itemInfo.member_price +=
                        itemModifier.price_default *
                        itemModifier.quantity *
                        itemChoiceQty;
                    }
                  });
                }
              );
            }
          }

          // if (index3 != - 1) {
          //   parentModifierGroupItem.price_default += modifierGroupItem.price_default;
          // }

          this.checkAvailableAddToCart();
        }
      }
    },

    removeModifierOptionGroup(
      // eslint-disable-next-line no-unused-vars
      indexChoice = -1,
      index,
      index2,
      index3 = -1,
      index4 = -1
    ) {
      // Get modifier group and item based on nesting level
      const parentItem = this.itemInfo.modifier_group[index].items[index2];
      const isNested = index3 !== -1;
      
      const modifierGroup = isNested
        ? parentItem.item_in_mog.modifier_group[index3]
        : this.itemInfo.modifier_group[index];
      
      const modifierGroupItem = isNested
        ? parentItem.item_in_mog.modifier_group[index3].items[index4]
        : this.itemInfo.modifier_group[index].items[index2];
      
      const parentTotalSelected = isNested ? (parentItem.quantity ?? 1) : 1;

      if (!modifierGroup || !modifierGroupItem) {
        return;
      }

      const currentQuantity = modifierGroupItem.quantity;
      const pricePerUnit = modifierGroupItem.price_default * parentTotalSelected;

      if (currentQuantity > 1) {
        // Decrement quantity
        modifierGroupItem.quantity -= 1;
        modifierGroup.total_selected -= 1;
        this.itemInfo.member_price -= pricePerUnit;
        this.itemInfo.price_default -= pricePerUnit;
      } else {
        // Uncheck modifier when quantity <= 1
        const totalPriceToDeduct = pricePerUnit * currentQuantity;
        
        modifierGroupItem.selected = false;
        modifierGroupItem.quantity = 1;
        modifierGroup.total_selected -= currentQuantity;
        this.itemInfo.member_price -= totalPriceToDeduct;
        this.itemInfo.price_default -= totalPriceToDeduct;

        // Handle nested modifiers for top-level items only
        if (!isNested && modifierGroupItem.item_in_mog?.modifier_group) {
          modifierGroupItem.item_in_mog.modifier_group.forEach((group) => {
            group.items.forEach((itemModifier) => {
              if (itemModifier.selected) {
                const nestedPrice = itemModifier.price_default * itemModifier.quantity * currentQuantity;
                itemModifier.selected = false;
                this.itemInfo.price_default -= nestedPrice;
                this.itemInfo.member_price -= nestedPrice;
              }
            });
          });
          modifierGroupItem.item_in_mog = {};
        }

        // Remove from items_selected array if exists
        if (modifierGroup.items_selected) {
          const targetIndex = isNested ? index4 : index2;
          const indexToRemove = modifierGroup.items_selected.indexOf(targetIndex);
          if (indexToRemove !== -1) {
            modifierGroup.items_selected.splice(indexToRemove, 1);
          }
        }
      }

      this.checkAvailableAddToCart();
    },

    removeModifierOptionGroupChoice(
      indexChoice = -1,
      index,
      index2,
      index3 = -1,
      index4 = -1
    ) {
      let itemSelect = this.choiceSelected.items[indexChoice].item_detail;
      const itemChoiceQty = this.choiceSelected.items[indexChoice].qty;

      // Determine context (nested or top-level)
      const parentItem = itemSelect.modifier_group[index].items[index2];
      const isNested = index3 !== -1;

      const modifierGroup = isNested
        ? parentItem.item_in_mog?.modifier_group?.[index3]
        : itemSelect.modifier_group[index];

      const modifierGroupItem = isNested
        ? parentItem.item_in_mog?.modifier_group?.[index3]?.items?.[index4]
        : itemSelect.modifier_group[index].items[index2];

      const parentTotalSelected = isNested ? (parentItem.quantity ?? 1) : 1;

      if (!modifierGroup || !modifierGroupItem) {
        return;
      }

      const currentQuantity = modifierGroupItem.quantity;
      const pricePerUnit =
        modifierGroupItem.price_default * parentTotalSelected * itemChoiceQty;

      if (currentQuantity > 1) {
        // Decrement quantity
        modifierGroupItem.quantity -= 1;
        modifierGroup.total_selected -= 1;
        this.itemInfo.member_price -= pricePerUnit;
        this.itemInfo.price_default -= pricePerUnit;
      } else {
        // Uncheck modifier when quantity <= 1
        const totalPriceToDeduct = pricePerUnit * currentQuantity;

        modifierGroupItem.selected = false;
        modifierGroupItem.quantity = 1;
        modifierGroup.total_selected -= currentQuantity;
        this.itemInfo.member_price -= totalPriceToDeduct;
        this.itemInfo.price_default -= totalPriceToDeduct;

        // Handle nested modifiers for top-level items only
        if (!isNested && modifierGroupItem.item_in_mog?.modifier_group) {
          modifierGroupItem.item_in_mog.modifier_group.forEach((group) => {
            group.items.forEach((itemModifier) => {
              if (itemModifier.selected) {
                const nestedPrice =
                  itemModifier.price_default *
                  itemModifier.quantity *
                  currentQuantity *
                  itemChoiceQty;
                itemModifier.selected = false;
                this.itemInfo.price_default -= nestedPrice;
                this.itemInfo.member_price -= nestedPrice;
              }
            });
          });
          modifierGroupItem.item_in_mog = {};
        }

        // Remove from items_selected array if exists
        if (modifierGroup.items_selected) {
          const targetIndex = isNested ? index4 : index2;
          const indexToRemove = modifierGroup.items_selected.indexOf(targetIndex);
          if (indexToRemove !== -1) {
            modifierGroup.items_selected.splice(indexToRemove, 1);
          }
        }
      }

      this.checkAvailableAddToCart();
      this.saveCurrentChoiceState();
    },

    async selectOnlyItem(
      indexChoice = -1,
      index,
      index2,
      item,
      index3 = -1,
      index4 = -1
    ) {
      // eslint-disable-next-line no-unused-vars
      const _ = indexChoice;
      let modifierGroup = this.itemInfo.modifier_group[index];
      let modifierGroupItem = this.itemInfo.modifier_group[index].items[index2];

      if (index3 == -1) {
        modifierGroup.items.forEach((it) => {
          it.is_show_modifier = false;
        });

        modifierGroupItem.is_show_modifier = !modifierGroupItem.is_show_modifier;
      } else {
        modifierGroup = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3];
        modifierGroupItem = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (!modifierGroupItem.is_not_available) {
          if (!modifierGroupItem.selected) {
            this.itemInfo.member_price +=
              modifierGroupItem.price_default * modifierGroupItem.quantity;
            this.itemInfo.price_default +=
              modifierGroupItem.price_default * modifierGroupItem.quantity;
          }

          const targetIdx = index3 == -1 ? index2 : index4;
          for (let idx = 0; idx < modifierGroup.items.length; idx++) {
            const itemModifier = modifierGroup.items[idx];
            if (idx != targetIdx) {
              if (itemModifier.selected) {
                this.itemInfo.member_price -=
                  itemModifier.price_default * itemModifier.quantity;
                this.itemInfo.price_default -=
                  itemModifier.price_default * itemModifier.quantity;

                if (
                  itemModifier.item_in_mog &&
                  itemModifier.item_in_mog.modifier_group
                ) {
                  itemModifier.item_in_mog.modifier_group.forEach(
                    (subGroup) => {
                      subGroup.items.forEach((subItemModifier) => {
                        if (subItemModifier.selected) {
                          subItemModifier.selected = false;
                          this.itemInfo.member_price -=
                            subItemModifier.price_default *
                            subItemModifier.quantity *
                            itemModifier.quantity;
                          this.itemInfo.price_default -=
                            subItemModifier.price_default *
                            subItemModifier.quantity *
                            itemModifier.quantity;
                        }
                      });
                    }
                  );
                }

                modifierGroup.items[idx].quantity = 1;
              }
              itemModifier.selected = false;
            } else {
              if (!itemModifier.selected || item.first_load) {
                itemModifier.selected = true;

                // first_load re-runs this for options that are ALREADY selected
                // (KioskItemModifierNew's keyReload watcher). Re-fetching would
                // replace item_in_mog with a fresh, nothing-selected list and
                // wipe the nested choices restored when editing from the cart,
                // so only fetch when the option has no nested list yet.
                if (index3 == -1 && !this.hasNestedOptions(modifierGroupItem)) {
                  modifierGroupItem.item_in_mog = await this.gotoOrderDetail(
                    this.categoryId,
                    item.id,
                    item
                  );
                }
              }
            }
          }

          // Sync group selection state for single (1/1) groups so that
          // addToCart() filter (which checks total_selected == 0) keeps the group.
          modifierGroup.total_selected = 1;
          modifierGroup.items_selected = [targetIdx];
        }
      }

      this.checkAvailableAddToCart();
    },

    async selectOnlyItemChoice(
      indexChoice = -1,
      index,
      index2,
      item,
      index3 = -1,
      index4 = -1
    ) {
      let itemSelect = this.choiceSelected.items[indexChoice].item_detail;
      const itemChoiceQty = this.choiceSelected.items[indexChoice].qty;
      let modifierGroup = itemSelect.modifier_group[index];
      let modifierGroupItem = itemSelect.modifier_group[index].items[index2];

      if (index3 == -1) {
        modifierGroup.items.forEach((it) => {
          it.is_show_modifier = false;
        });

        modifierGroupItem.is_show_modifier = !modifierGroupItem.is_show_modifier;
      } else {
        modifierGroup =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3];
        modifierGroupItem =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (!modifierGroupItem.is_not_available) {
          if (!modifierGroupItem.selected) {
            this.itemInfo.member_price +=
              modifierGroupItem.price_default *
              modifierGroupItem.quantity *
              itemChoiceQty;
            this.itemInfo.price_default +=
              modifierGroupItem.price_default *
              modifierGroupItem.quantity *
              itemChoiceQty;
          }

          const targetIdx = index3 == -1 ? index2 : index4;
          for (let idx = 0; idx < modifierGroup.items.length; idx++) {
            const itemModifier = modifierGroup.items[idx];
            if (idx != targetIdx) {
              if (itemModifier.selected) {
                this.itemInfo.member_price -=
                  itemModifier.price_default *
                  itemModifier.quantity *
                  itemChoiceQty;
                this.itemInfo.price_default -=
                  itemModifier.price_default *
                  itemModifier.quantity *
                  itemChoiceQty;

                if (
                  itemModifier.item_in_mog &&
                  itemModifier.item_in_mog.modifier_group
                ) {
                  itemModifier.item_in_mog.modifier_group.forEach(
                    (subGroup) => {
                      subGroup.items.forEach((subItemModifier) => {
                        if (subItemModifier.selected) {
                          subItemModifier.selected = false;
                          this.itemInfo.member_price -=
                            subItemModifier.price_default *
                            subItemModifier.quantity *
                            itemModifier.quantity *
                            itemChoiceQty;
                          this.itemInfo.price_default -=
                            subItemModifier.price_default *
                            subItemModifier.quantity *
                            itemModifier.quantity *
                            itemChoiceQty;
                        }
                      });
                    }
                  );
                }

                modifierGroup.items[idx].quantity = 1;
              }
              itemModifier.selected = false;
            } else {
              if (!itemModifier.selected || item.first_load) {
                itemModifier.selected = true;

                // first_load re-runs this for options that are ALREADY selected
                // (KioskItemModifierNew's keyReload watcher). Re-fetching would
                // replace item_in_mog with a fresh, nothing-selected list and
                // wipe the nested choices restored when editing from the cart,
                // so only fetch when the option has no nested list yet.
                if (index3 == -1 && !this.hasNestedOptions(modifierGroupItem)) {
                  modifierGroupItem.item_in_mog = await this.gotoOrderDetail(
                    this.categoryId,
                    item.id,
                    item,
                    itemChoiceQty
                  );
                }
              }
            }
          }

          // Sync group selection state for single (1/1) groups so that
          // addToCart() filter (which checks total_selected == 0) keeps the group.
          modifierGroup.total_selected = 1;
          modifierGroup.items_selected = [targetIdx];
        }
      }

      this.checkAvailableAddToCart();
      this.saveCurrentChoiceState();
    },

    async gotoOrderDetail(categoryId, id, item, itemChoiceQty = 1) {
      // console.log(stock_balance, is_not_available, stock_enable_balance, item)
      // if(this.checkAvailableItem(item) && item.isHaveModifierOptionGroups > 0) {
      let data = {};

      if (this.checkAvailableItem(item)) {
        this.categoryIdSelect = categoryId;
        this.itemIdSelect = id;
        // this.showLoader();
        let dataForm = {
          menuset_id: this.branch.menuset_id ?? 0,
          category_id: categoryId,
          item_id: id,
          branch_code: this.branch.branch_code ?? "",
          take_away_or_dine_in: getDiningOption(),
          is_kiosk: 1,
        };
        await OrderService.getItemDetail(dataForm).then(
          (response) => {
            if (response.data.result.isSuccess) {
              if (
                response.data.data.modifier_group &&
                response.data.data.modifier_group.length > 0
              ) {
                data = response.data.data;

                // Kiosk: disable backend auto-select on nested modifiers too,
                // and reset data prices to base so the diff added below is 0.
                this.disableAutoSelectInModifierGroup(data.modifier_group);
                data.price_default = parseFloat(
                  data.price_default_no_modifier_option || 0
                );
                data.member_price = parseFloat(
                  data.member_price_no_modifier_option || 0
                );

                this.itemInfo.price_default +=
                  (data.price_default - data.price_default_no_modifier_option) *
                  itemChoiceQty;
                this.itemInfo.member_price +=
                  (data.price_default - data.price_default_no_modifier_option) *
                  itemChoiceQty;
              }
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
            }
            this.isLoadDone = true;
            // this.hideLoader();
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error;
            // this.hideLoader();
          }
        );
      }

      return data;
    },

    async selectMultiItem(
      // eslint-disable-next-line no-unused-vars
      indexChoice = -1,
      index,
      index2,
      item,
      index3 = -1,
      index4 = -1
    ) {
      let modifierGroup = this.itemInfo.modifier_group[index];
      let modifierGroupItem = this.itemInfo.modifier_group[index].items[index2];
      let parentTotalSelectd = 1;

      if (index3 == -1) {
        modifierGroupItem.is_show_modifier = !modifierGroupItem.is_show_modifier;
      } else {
        parentTotalSelectd =
          this.itemInfo.modifier_group[index].items[index2].quantity ?? 1;
        modifierGroup = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3];
        modifierGroupItem = this.itemInfo.modifier_group[index].items[index2]
          .item_in_mog.modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (!modifierGroupItem.is_not_available) {
          if (
            !modifierGroupItem.selected &&
            modifierGroup.total_selected >= modifierGroup.max_qty
          ) {
            return;
          }

          modifierGroupItem.selected = !modifierGroupItem.selected;
          const modifierGroupItemQty = modifierGroupItem.quantity;

          if (modifierGroupItem.selected) {
            modifierGroup.total_selected += 1;
            this.itemInfo.member_price +=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd;
            this.itemInfo.price_default +=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd;

            // Add to items_selected if not already present
            const targetIndex = index3 == -1 ? index2 : index4;
            if (modifierGroup.items_selected && modifierGroup.items_selected.indexOf(targetIndex) === -1) {
              modifierGroup.items_selected.push(targetIndex);
            }

            if (index3 == -1) {
              modifierGroupItem.item_in_mog = await this.gotoOrderDetail(
                this.categoryId,
                item.id,
                item
              );
            }
          } else {
            modifierGroup.total_selected -= modifierGroupItemQty;
            this.itemInfo.member_price -=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd;
            this.itemInfo.price_default -=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd;

            if (index3 == -1) {
              if (
                modifierGroupItem.item_in_mog &&
                modifierGroupItem.item_in_mog.modifier_group
              ) {
                modifierGroupItem.item_in_mog.modifier_group.forEach(
                  (group) => {
                    group.items.forEach((itemModifier) => {
                      if (itemModifier.selected) {
                        itemModifier.selected = false;
                        this.itemInfo.price_default -=
                          itemModifier.price_default *
                          itemModifier.quantity *
                          modifierGroupItemQty;
                        this.itemInfo.member_price -=
                          itemModifier.price_default *
                          itemModifier.quantity *
                          modifierGroupItemQty;
                      }
                    });
                  }
                );
              }

              modifierGroupItem.item_in_mog = {};
            }

            modifierGroupItem.quantity = 1;
            
            // Remove from items_selected array
            const targetIndex = index3 == -1 ? index2 : index4;
            if (modifierGroup.items_selected) {
              const indexToRemove = modifierGroup.items_selected.indexOf(targetIndex);
              if (indexToRemove !== -1) {
                modifierGroup.items_selected.splice(indexToRemove, 1);
              }
            }
          }

          this.checkAvailableAddToCart();
        }
      }
    },

    async selectMultiItemChoice(
      indexChoice = -1,
      index,
      index2,
      item,
      index3 = -1,
      index4 = -1
    ) {
      let itemSelect = this.choiceSelected.items[indexChoice].item_detail;
      const itemChoiceQty = this.choiceSelected.items[indexChoice].qty;

      let modifierGroup = itemSelect.modifier_group[index];
      let modifierGroupItem = itemSelect.modifier_group[index].items[index2];
      let parentTotalSelectd = 1;

      if (index3 == -1) {
        modifierGroupItem.is_show_modifier = !modifierGroupItem.is_show_modifier;
      } else {
        parentTotalSelectd = itemSelect.modifier_group[index].items[index2].quantity ?? 1;
        modifierGroup =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3];
        modifierGroupItem =
          itemSelect.modifier_group[index].items[index2].item_in_mog
            .modifier_group[index3].items[index4];
      }

      if (modifierGroup && modifierGroupItem) {
        if (!modifierGroupItem.is_not_available) {
          if (
            !modifierGroupItem.selected &&
            modifierGroup.total_selected >= modifierGroup.max_qty
          ) {
            return;
          }

          modifierGroupItem.selected = !modifierGroupItem.selected;
          const modifierGroupItemQty = modifierGroupItem.quantity;

          if (modifierGroupItem.selected) {
            modifierGroup.total_selected += 1;
            this.itemInfo.member_price +=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd *
              itemChoiceQty;
            this.itemInfo.price_default +=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd *
              itemChoiceQty;

            // Add to items_selected if not already present
            const targetIndex = index3 == -1 ? index2 : index4;
            if (modifierGroup.items_selected && modifierGroup.items_selected.indexOf(targetIndex) === -1) {
              modifierGroup.items_selected.push(targetIndex);
            }

            if (index3 == -1) {
              modifierGroupItem.item_in_mog = await this.gotoOrderDetail(
                this.categoryId,
                item.id,
                item,
                itemChoiceQty
              );
            }
          } else {
            modifierGroup.total_selected -= modifierGroupItem.quantity;
            this.itemInfo.member_price -=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd *
              itemChoiceQty;
            this.itemInfo.price_default -=
              modifierGroupItem.price_default *
              modifierGroupItemQty *
              parentTotalSelectd *
              itemChoiceQty;

            if (index3 == -1) {
              if (
                modifierGroupItem.item_in_mog &&
                modifierGroupItem.item_in_mog.modifier_group
              ) {
                modifierGroupItem.item_in_mog.modifier_group.forEach(
                  (group) => {
                    group.items.forEach((itemModifier) => {
                      if (itemModifier.selected) {
                        itemModifier.selected = false;
                        this.itemInfo.price_default -=
                          itemModifier.price_default *
                          itemModifier.quantity *
                          modifierGroupItemQty *
                          itemChoiceQty;
                        this.itemInfo.member_price -=
                          itemModifier.price_default *
                          itemModifier.quantity *
                          modifierGroupItemQty *
                          itemChoiceQty;
                      }
                    });
                  }
                );
              }

              modifierGroupItem.item_in_mog = {};
            }

            modifierGroupItem.quantity = 1;
            
            // Remove from items_selected array
            const targetIndex = index3 == -1 ? index2 : index4;
            if (modifierGroup.items_selected) {
              const indexToRemove = modifierGroup.items_selected.indexOf(targetIndex);
              if (indexToRemove !== -1) {
                modifierGroup.items_selected.splice(indexToRemove, 1);
              }
            }
          }

          this.checkAvailableAddToCart();
          this.saveCurrentChoiceState();
        }
      }
    },

    addItem() {
      this.itemInfo.quantity += 1;
    },

    removeItem() {
      this.itemInfo.quantity -= 1;
    },

    addToCart() {
      // A By Weight item is priced from the scale (weight × price_per_kg), never
      // from its catalog price — merchants routinely put the per-100g figure in
      // price 1, so an item that reached this popup without going through
      // ModalWeighing would be charged that figure and skip the minimum
      // entirely. Refuse it here, where every add-to-cart path converges.
      if (this.itemInfo.is_by_weight) {
        const weighed = parseFloat(this.itemInfo.weight_kg) || 0;
        const lineTotal = parseFloat(this.itemInfo.weight_line_total) || 0;

        if (weighed <= 0) {
          this.$emit(
            "show-error",
            this.$t("kiosk.itemDetail.weighFirst")
          );
          return;
        }

        if (lineTotal < KIOSK_WEIGH_MIN_ADD_CART) {
          this.$emit(
            "show-error",
            `Minimum $${KIOSK_WEIGH_MIN_ADD_CART.toFixed(2)} to add to cart.`
          );
          return;
        }
      }

      if (this.itemInfo.disable_add_to_cart) {
        this.showUnmetRequired();
      } else {
        if (this.itemInfo.choice) {
          // Sub-category: only the single selected item goes into the cart payload.
          if (this.itemInfo.is_sub_category) {
            const idx = this.selectedSubCategoryIndex;
            if (idx >= 0 && this.choiceSelected.items[idx]) {
              this.choiceSelected.items = [this.choiceSelected.items[idx]];
            }
          }
          this.choiceSelected.items.forEach((item) => {
            let itemInfo = item.item_detail;
            let checkOrderSuccess = true;
            // let modifier_group_full = JSON.parse(JSON.stringify(itemInfo.modifier_group));

            // itemInfo.modifier_group_full = modifier_group_full;

            itemInfo.modifier_group.forEach((item) => {
              if (item.total_selected < item.min_qty) {
                checkOrderSuccess = false;
                return;
              }
            });

            if (!checkOrderSuccess) {
              return;
            }

            itemInfo.modifier_group = itemInfo.modifier_group.filter(
              (modifierGroup, index) => {
                if (modifierGroup.total_selected == 0) {
                  return false;
                } else {
                  itemInfo.modifier_group[
                    index
                  ].items = modifierGroup.items.filter((item) => {
                    if (!item.selected) {
                      return false;
                    }

                    if (item.item_in_mog && item.item_in_mog.modifier_group) {
                      item.item_in_mog.modifier_group = item.item_in_mog.modifier_group.filter(
                        (subModifierGroup, subIndex) => {
                          if (subModifierGroup.total_selected == 0) {
                            return false;
                          } else {
                            item.item_in_mog.modifier_group[
                              subIndex
                            ].items = subModifierGroup.items.filter(
                              (subItem) => {
                                if (!subItem.selected) {
                                  return false;
                                }

                                return true;
                              }
                            );
                          }

                          return true;
                        }
                      );
                    }

                    return true;
                  });
                }

                return true;
              }
            );

            // itemInfo.modifier_group_full = itemInfo.modifier_group_full.filter(
            //   (modifierGroup, index) => {
            //     if (modifierGroup.total_selected == 0) {
            //       return false;
            //     } else {
            //       itemInfo.modifier_group_full[
            //         index
            //       ].items = modifierGroup.items.filter((itemMog) => {
            //         if (itemMog.selected) {
            //           return false;
            //         }

            //         return true;
            //       });
            //     }

            //     return true;
            //   }
            // );
          });
        } else {
          let checkOrderSuccess = true;
          //let modifier_group_full = JSON.parse(JSON.stringify(this.itemInfo.modifier_group));

          //this.itemInfo.modifier_group_full = modifier_group_full;

          this.itemInfo.modifier_group.forEach((item) => {
            if (item.total_selected < item.min_qty) {
              checkOrderSuccess = false;
              return;
            }
          });

          if (!checkOrderSuccess) {
            return;
          }

          this.itemInfo.modifier_group = this.itemInfo.modifier_group.filter(
            (modifierGroup, index) => {
              if (modifierGroup.total_selected == 0) {
                return false;
              } else {
                this.itemInfo.modifier_group[
                  index
                ].items = modifierGroup.items.filter((item) => {
                  if (!item.selected) {
                    return false;
                  }

                  if (item.item_in_mog && item.item_in_mog.modifier_group) {
                    item.item_in_mog.modifier_group = item.item_in_mog.modifier_group.filter(
                      (subModifierGroup, subIndex) => {
                        if (subModifierGroup.total_selected == 0) {
                          return false;
                        } else {
                          item.item_in_mog.modifier_group[
                            subIndex
                          ].items = subModifierGroup.items.filter((subItem) => {
                            if (!subItem.selected) {
                              return false;
                            }

                            return true;
                          });
                        }

                        return true;
                      }
                    );
                  }

                  return true;
                });
              }

              return true;
            }
          );

          // this.itemInfo.modifier_group_full = this.itemInfo.modifier_group_full.filter(
          //   (modifierGroup, index) => {
          //     if (modifierGroup.total_selected == 0) {
          //       return false;
          //     } else {
          //       this.itemInfo.modifier_group_full[
          //         index
          //       ].items = modifierGroup.items.filter((item) => {
          //         if (item.selected) {
          //           return false;
          //         }

          //         return true;
          //       });
          //     }

          //     return true;
          //   }
          // );
        }

        this.itemInfo.choice_selected = this.choiceSelected;
        this.itemInfo.choices = null;

        // Step 5: Save category_id to itemInfo so it can be edited later
        this.itemInfo.category_id = this.categoryId;

        // Step 4: Handle edit mode - if editing an item in the cart
        if (this.editMode && this.editItemIndex >= 0) {
          // Emit event to update item in cart instead of adding new
          this.$emit('item-updated', this.itemInfo);

          EventBus.emit("AddToCartSuccess");
          EventBus.emit("AddToCartThenReloadCategorySuccess", {
            time: this.reloadModal,
          });
          this.closeModel();
          return; // Stop here, don't execute normal add to cart logic
        }

        // Normal add to cart logic (when not in edit mode)
        let orderInfo =
          JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
        const objectInArrayIndex = orderInfo.findIndex((item) => {
          if (this.compareObjects(item, this.itemInfo)) {
            item.quantity += this.itemInfo.quantity;
            return true;
          }
          return false;
        });
        if (objectInArrayIndex !== -1) {
          localStorage.removeItem(this.cartName);
          localStorage.setItem(this.cartName, JSON.stringify(orderInfo));
        } else {
          orderInfo.push(this.itemInfo);
          localStorage.setItem(this.cartName, JSON.stringify(orderInfo));
        }
        EventBus.emit("AddToCartSuccess");
        EventBus.emit("AddToCartThenReloadCategorySuccess", {
          time: this.reloadModal,
        });
        this.closeModel();
      }
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    clickLink(slide) {
      if (slide && slide.destination_link != "") {
        window.open(slide.destination_link);
      } else if (
        slide &&
        slide.direct_to_menu_item != "" &&
        !slide.direct_to_suggested_adds_on
      ) {
        this.showLoader();
        let dataForm = {
          menuset_id: this.branch.menuset_id ?? 0,
          category_id: this.categoryId ?? 0,
          item_id: slide.direct_to_menu_item ?? 0,
          branch_code: this.$store.state.branchCode,
          take_away_or_dine_in: getDiningOption(),
          is_kiosk: 1,
        };

        OrderService.getItemDetail(dataForm).then(
          (response) => {
            if (response.data.result.isSuccess) {
              this.isLoadDone = true;
              this.itemInfo = response.data.data;
              this.itemInfo.price_default = parseFloat(
                this.itemInfo.price_default
              );
              this.itemInfo.member_price = parseFloat(
                this.itemInfo.member_price
              );
            } else {
              this.snackbar = "danger";
              this.message = response.data.result.message;
              this.setTimeOutNotification();
            }
            this.isLoadDone = true;
            this.hideLoader();
            this.resetSheetUi();
          },
          (error) => {
            this.snackbar = "danger";
            this.message = error;
            this.hideLoader();
          }
        );
      }
    },

    getModifierToShowSlide(modifierGroups) {
      // Iterate through modifier groups
      for (const group of modifierGroups) {
        // Find item with price > 0
        const itemWithPrice = group.items.find((subItem) => subItem.price > 0);
        if (itemWithPrice) {
          return itemWithPrice;
        }
      }

      // If no item with price > 0 return the first item from the first group
      const firstItem = modifierGroups[0].items[0];

      return firstItem;
    },

    calculateTotalStockBalanceForFoundItems(itemInfo) {
      let orderInfo =
        JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
      let totalStockBalance = 0;
      for (let item of orderInfo) {
        if (item.id === itemInfo.id) {
          totalStockBalance += item.quantity;
        }
      }
      return totalStockBalance;
    },

    calculateTotalStockBalanceForFoundItemsModifier(itemInfo) {
      let orderInfo =
        JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
      let totalStockBalance = 0;
      for (let i = 0; i < orderInfo.length; i++) {
        if (orderInfo[i] && orderInfo[i].modifier_group) {
          for (let j = 0; j < orderInfo[i].modifier_group.length; j++) {
            if (orderInfo[i].modifier_group[j].items) {
              for (
                let k = 0;
                k < orderInfo[i].modifier_group[j].items.length;
                k++
              ) {
                if (
                  orderInfo[i].modifier_group[j].items[k].id === itemInfo.id
                ) {
                  totalStockBalance +=
                    orderInfo[i].modifier_group[j].items[k].quantity;
                  // return;
                }
              }
            }
          }
        }
      }
      return totalStockBalance;
    },

    checkAvailableItem() {
      const item = this.itemInfo;
      var stockBalance =
        this.itemInfo.stock_balance -
        this.calculateTotalStockBalanceForFoundItems(this.itemInfo);

      if (item.stock_enable_balance) {
        // countdown = true
        if (item.stock_balance > 0) {
          if (stockBalance > 0) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        // countdown = false
        if (item.is_not_available == 0) {
          return true;
        } else {
          return false;
        }
      }
    },

    checkAvailableModifierItem(item) {
      var stockBalance =
        item.stock_balance -
        this.calculateTotalStockBalanceForFoundItemsModifier(item);
      if (item.stock_enable_balance) {
        // countdown = true
        if (item.stock_balance > 0) {
          if (stockBalance > 0) {
            return false;
          } else {
            return true;
          }
        } else {
          return true;
        }
      } else {
        // countdown = false
        if (item.is_not_available == 0) {
          return false;
        } else {
          return true;
        }
      }
    },

    checkAvailableAddToCart() {
      // Recompute price from scratch to keep `price_default` in sync with
      // currently selected modifiers — guards against any drift in the
      // incremental ADD/SUBTRACT inside select/add/remove handlers.
      this.recalcAllPrices();

      let check = false;

      if (this.itemInfo.is_sub_category) {
        // Only the selected sub-category item's modifier groups gate add-to-cart.
        const idx = this.selectedSubCategoryIndex;
        const items = this.choiceSelected && this.choiceSelected.items;
        const sel = idx >= 0 && items && items[idx] ? items[idx].item_detail : null;

        if (!sel) {
          check = true;
        } else if (Array.isArray(sel.modifier_group)) {
          sel.modifier_group.forEach((group) => {
            const selectedQtySum = Array.isArray(group.items)
              ? group.items
                  .filter((it) => it.selected)
                  .reduce((sum, it) => sum + (it.quantity || 1), 0)
              : 0;

            group.total_selected = selectedQtySum;

            if (selectedQtySum < group.min_qty) {
              check = true;
            }
          });
        }
      } else if (this.itemInfo.choice) {
        this.choiceSelected.items.forEach((item) => {
          let itemInfo = item.item_detail;

          if (!itemInfo) {
            return;
          }

          itemInfo.modifier_group.forEach((group) => {
            const selectedQtySum = Array.isArray(group.items)
              ? group.items
                  .filter((it) => it.selected)
                  .reduce((sum, it) => sum + (it.quantity || 1), 0)
              : 0;

            // Sync total_selected with the total selected quantity (not item count),
            // so the max_qty/min_qty checks against total_selected stay consistent.
            group.total_selected = selectedQtySum;

            if (selectedQtySum < group.min_qty) {
              check = true;
            }
          });
        });
      } else {
        this.itemInfo.modifier_group.forEach((group) => {
          const selectedQtySum = Array.isArray(group.items)
            ? group.items
                .filter((it) => it.selected)
                .reduce((sum, it) => sum + (it.quantity || 1), 0)
            : 0;

          // Sync total_selected with the total selected quantity (not item count),
          // so the max_qty/min_qty checks against total_selected stay consistent.
          group.total_selected = selectedQtySum;

          if (selectedQtySum < group.min_qty) {
            check = true;
          }
        });
      }

      this.itemInfo.disable_add_to_cart = check;
    },

    // Ensure required groups (min_qty >=1, max_qty ==1) always have defaults if not selected
    ensureDefaultForRequiredGroups(modifierGroups) {
      if (!Array.isArray(modifierGroups)) return;

      modifierGroups.forEach((group) => {
        if (
          group &&
          group.min_qty >= 1 &&
          group.max_qty === 1 &&
          Array.isArray(group.items)
        ) {
          const hasSelected = group.items.some((it) => it.selected);

          if (!hasSelected) {
            const firstAvailableIndex = group.items.findIndex(
              (it) => !it.is_not_available
            );
            if (firstAvailableIndex !== -1) {
              group.items.forEach((it, idx) => {
                it.selected = idx === firstAvailableIndex;
                it.quantity = 1;
              });
              group.total_selected = 1;
              group.items_selected = [firstAvailableIndex];
            }
          }
        }
      });
    },

    saveCurrentChoiceState() {
      if (this.itemInfo.choice && this.choiceSelected && this.choiceSelected.items) {
        // Save current choice state back to originalChoices
        const originalChoices = JSON.parse(this.originalChoices);
        originalChoices[this.selectedChoiceIndex] = JSON.parse(JSON.stringify(this.choiceSelected));
        this.originalChoices = JSON.stringify(originalChoices);
      }
    },

    async loadItemInMogForSelectedItems() {
      this.isLoadingModifiers = true;

      try {
        // Handle both regular items and choice items
        if (this.itemInfo.choice && this.choiceSelected.items) {
          // For choice items, check each item in the choice
          for (const choiceItem of this.choiceSelected.items) {
            if (choiceItem.item_detail && choiceItem.item_detail.modifier_group) {
              for (const modifierGroup of choiceItem.item_detail.modifier_group) {
                for (const item of modifierGroup.items) {
                  if (item.selected && (!item.item_in_mog || !item.item_in_mog.id)) {
                    // Load item_in_mog for selected items that don't have it
                    item.item_in_mog = await this.gotoOrderDetail(
                      this.categoryId,
                      item.id,
                      item,
                      choiceItem.qty
                    );
                  }
                }
              }
            }
          }
        } else if (this.itemInfo.modifier_group) {
          // For regular items
          for (const modifierGroup of this.itemInfo.modifier_group) {
            for (const item of modifierGroup.items) {
              if (item.selected && (!item.item_in_mog || !item.item_in_mog.id)) {
                // Load item_in_mog for selected items that don't have it
                item.item_in_mog = await this.gotoOrderDetail(
                  this.categoryId,
                  item.id,
                  item
                );
              }
            }
          }
        }

        // After all items are loaded, show modifier sections
        this.showModifierSectionsForSelectedItems();
        
        // Check if add to cart should be enabled after loading all data
        this.checkAvailableAddToCart();
      } finally {
        this.isLoadingModifiers = false;
      }
    },

    showModifierSectionsForSelectedItems() {
      // Force a re-render of the ItemModifier component to ensure proper display
      this.$nextTick(() => {
        this.itemModifierKey = Math.random();
      });
    },

    // Kiosk-only: undo backend auto-select for required (min=1, max=1) groups.
    // Backend marks first available item as selected and (for non-choice mode)
    // adds its price to itemInfo.price_default/member_price. We reverse both.
    disableAutoSelectInModifierGroup(modifierGroups) {
      if (!Array.isArray(modifierGroups)) return;
      modifierGroups.forEach((group) => {
        if (
          group &&
          group.min_qty === 1 &&
          group.max_qty === 1 &&
          Array.isArray(group.items)
        ) {
          group.items.forEach((it) => {
            it.selected = false;
          });
          group.items_selected = [];
          group.total_selected = 0;
        }
      });
    },

    disableAutoSelectFromAPI() {
      if (!this.itemInfo) return;

      if (this.itemInfo.choice) {
        // Choice mode: backend auto-selects but doesn't add price to itemInfo.
        // Just deselect within each choice item's modifier_group.
        if (Array.isArray(this.itemInfo.choices)) {
          this.itemInfo.choices.forEach((choice) => {
            if (!Array.isArray(choice.items)) return;
            choice.items.forEach((choiceItem) => {
              if (choiceItem.item_detail) {
                this.disableAutoSelectInModifierGroup(
                  choiceItem.item_detail.modifier_group
                );
              }
            });
          });
        }
      } else {
        // Non-choice: deselect AND reset prices to base.
        this.disableAutoSelectInModifierGroup(this.itemInfo.modifier_group);
        this.itemInfo.price_default = parseFloat(
          this.itemInfo.price_default_no_modifier_option || 0
        );
        this.itemInfo.member_price = parseFloat(
          this.itemInfo.member_price_no_modifier_option || 0
        );
      }
    },

    onBeforeItemClick(type, indexChoice, index, index2) {
      // eslint-disable-next-line no-unused-vars
      const _ = [type, indexChoice, index, index2];
      this.modifierOptionSnapshot = {
        itemInfo: JSON.parse(JSON.stringify(this.itemInfo)),
        choiceSelected: this.itemInfo.choice
          ? JSON.parse(JSON.stringify(this.choiceSelected))
          : null,
        originalChoices: this.originalChoices,
      };
    },

    onAfterItemClick(type, indexChoice, index, index2, wasSelected) {
      // eslint-disable-next-line no-unused-vars
      const _ = type;
      // Already-selected click → no modal (multi deselects, single is no-op).
      if (wasSelected) {
        this.modifierOptionSnapshot = null;
        return;
      }

      const itemSelect = this.itemInfo.choice
        ? this.choiceSelected &&
          this.choiceSelected.items &&
          this.choiceSelected.items[indexChoice] &&
          this.choiceSelected.items[indexChoice].item_detail
        : this.itemInfo;

      if (!itemSelect || !itemSelect.modifier_group) {
        this.modifierOptionSnapshot = null;
        return;
      }

      const updatedItem =
        itemSelect.modifier_group[index] &&
        itemSelect.modifier_group[index].items &&
        itemSelect.modifier_group[index].items[index2];

      const hasNested =
        updatedItem &&
        updatedItem.selected &&
        updatedItem.item_in_mog &&
        updatedItem.item_in_mog.modifier_group &&
        updatedItem.item_in_mog.modifier_group.length > 0;

      if (hasNested) {
        this.modifierOptionContext = { type, indexChoice, index, index2 };
        this.showModifierOptionModal = true;
      } else {
        this.modifierOptionSnapshot = null;
      }
    },

    async openShowMoreModifier(indexChoice, index, index2) {
      // Re-open the nested modifier modal for an already-selected option.
      const itemSelect = this.itemInfo.choice
        ? this.choiceSelected &&
          this.choiceSelected.items &&
          this.choiceSelected.items[indexChoice] &&
          this.choiceSelected.items[indexChoice].item_detail
        : this.itemInfo;
      if (!itemSelect || !itemSelect.modifier_group) return;
      const group = itemSelect.modifier_group[index];
      const item = group && group.items && group.items[index2];
      if (!item) return;

      // The cart stores only the selected nested options (addToCart filters them
      // out), so an item edited from the cart carries a partial item_in_mog.
      // Re-fetch the full option list and re-apply the saved selections so the
      // modal shows every choice, exactly like the first time it was opened.
      const itemChoiceQty = this.itemInfo.choice
        ? this.choiceSelected &&
          this.choiceSelected.items &&
          this.choiceSelected.items[indexChoice] &&
          this.choiceSelected.items[indexChoice].qty
        : 1;
      this.isLoadingModifiers = true;
      try {
        await this.reloadFullItemInMog(item, itemChoiceQty || 1);
      } finally {
        this.isLoadingModifiers = false;
      }

      // Snapshot current state so Cancel restores the nested selections.
      this.modifierOptionSnapshot = {
        itemInfo: JSON.parse(JSON.stringify(this.itemInfo)),
        choiceSelected: this.itemInfo.choice
          ? JSON.parse(JSON.stringify(this.choiceSelected))
          : null,
        originalChoices: this.originalChoices,
      };
      this.modifierOptionContext = { type: "multi", indexChoice, index, index2 };
      this.showModifierOptionModal = true;
    },

    // True when a modifier option already carries a nested option list — either
    // fetched earlier in this session or restored from the cart when editing.
    hasNestedOptions(modifierItem) {
      return !!(
        modifierItem &&
        modifierItem.item_in_mog &&
        Array.isArray(modifierItem.item_in_mog.modifier_group) &&
        modifierItem.item_in_mog.modifier_group.length > 0
      );
    },

    // Fetch the full nested option list for a selected modifier item and merge
    // the currently-saved selections into it (without touching prices — the
    // saved selections are already reflected in itemInfo's totals).
    async reloadFullItemInMog(item, itemChoiceQty) {
      if (!item || !item.selected) return;
      if (!item.item_in_mog || !Array.isArray(item.item_in_mog.modifier_group)) {
        return;
      }

      const full = await this.gotoOrderDetail(
        this.categoryId,
        item.id,
        item,
        itemChoiceQty
      );
      // Apply synchronously (no await between reading and swapping the option
      // list) so the merge is atomic on the item object.
      this.applyFullItemInMog(item, full);
    },

    // Merge saved selections into the freshly-fetched full option list, then
    // swap in the complete modifier_group (keeping the existing item_in_mog
    // reference so we only replace the option list, not the whole object).
    applyFullItemInMog(item, full) {
      if (!full || !Array.isArray(full.modifier_group)) return;
      if (!item.item_in_mog || !Array.isArray(item.item_in_mog.modifier_group)) {
        return;
      }
      this.mergeNestedSelections(full.modifier_group, item.item_in_mog.modifier_group);
      item.item_in_mog.modifier_group = full.modifier_group;
    },

    // Copy selected/quantity/total_selected state from savedGroups (partial,
    // selected-only) onto fullGroups (the complete option list). items_selected
    // is recomputed against the full list's item positions.
    mergeNestedSelections(fullGroups, savedGroups) {
      if (!Array.isArray(fullGroups) || !Array.isArray(savedGroups)) return;

      fullGroups.forEach((fullGroup) => {
        const savedGroup = savedGroups.find((g) => g.id === fullGroup.id);
        const itemsSelected = [];

        (fullGroup.items || []).forEach((fullItem, idx) => {
          const savedItem =
            savedGroup && Array.isArray(savedGroup.items)
              ? savedGroup.items.find((it) => it.id === fullItem.id)
              : null;

          if (savedItem && savedItem.selected) {
            fullItem.selected = true;
            fullItem.quantity = savedItem.quantity || 1;
            itemsSelected.push(idx);
          } else {
            fullItem.selected = false;
            fullItem.quantity = 1;
          }
        });

        fullGroup.items_selected = itemsSelected;
        fullGroup.total_selected =
          savedGroup && typeof savedGroup.total_selected === "number"
            ? savedGroup.total_selected
            : itemsSelected.length;
      });
    },

    confirmModifierOption() {
      this.showModifierOptionModal = false;
      this.modifierOptionContext = null;
      this.modifierOptionSnapshot = null;
      this.checkAvailableAddToCart();
    },

    cancelModifierOption() {
      // Restore state taken before the parent item click.
      if (this.modifierOptionSnapshot) {
        this.itemInfo = this.modifierOptionSnapshot.itemInfo;
        if (this.modifierOptionSnapshot.choiceSelected) {
          this.choiceSelected = this.modifierOptionSnapshot.choiceSelected;
        }
        if (this.modifierOptionSnapshot.originalChoices) {
          this.originalChoices = this.modifierOptionSnapshot.originalChoices;
        }
        this.modifierOptionSnapshot = null;
        this.itemModifierKey = Math.random();
      }
      this.showModifierOptionModal = false;
      this.modifierOptionContext = null;
      this.checkAvailableAddToCart();
    },

    compareObjects(obj1, obj2) {
      if (obj1.choice != obj2.choice) {
        return false;
      }

      // Check main item ID
      if (obj1.id !== obj2.id) {
        return false;
      }

      // Check main item price
      if (this.isLogined) {
        if (obj1.member_price != obj2.member_price) {
          return false;
        }
      } else {
        if (obj1.price_default != obj2.price_default) {
          return false;
        }
      }

      if (obj2.choice) {
        // Compare choice_selected
        if (
          obj1.choice_selected.id !== obj2.choice_selected.id ||
          obj1.choice_selected.default_price !==
            obj2.choice_selected.default_price ||
          obj1.choice_selected.member_price !==
            obj2.choice_selected.member_price
        ) {
          return false;
        }

        // Compare items in choice_selected
        const items1 = obj1.choice_selected.items;
        const items2 = obj2.choice_selected.items;

        for (let i = 0; i < items1.length; i++) {
          if (
            items1[i].id !== items2[i].id ||
            items1[i].qty !== items2[i].qty
          ) {
            return false;
          }

          let itemDetail1 = items1[i].item_detail;
          let itemDetail2 = items2[i].item_detail;

          // Compare modifiers in item
          if (itemDetail1?.modifier_group && itemDetail2?.modifier_group) {
            if (
              itemDetail1.modifier_group.length !=
              itemDetail2.modifier_group.length
            ) {
              return false;
            }

            for (let j = 0; j < itemDetail1.modifier_group.length; j++) {
              if (
                itemDetail1.modifier_group[j].id !=
                  itemDetail2.modifier_group[j].id ||
                itemDetail1.modifier_group[j].total_selected !=
                  itemDetail2.modifier_group[j].total_selected
              ) {
                return false;
              }

              let itemModifiers1 = itemDetail1.modifier_group[j].items;
              let itemModifiers2 = itemDetail2.modifier_group[j].items;

              if (itemModifiers1.length != itemModifiers2.length) {
                return false;
              }

              for (let k = 0; k < itemModifiers1.length; k++) {
                if (
                  itemModifiers1[k].id != itemModifiers2[k].id ||
                  itemModifiers1[k].quantity != itemModifiers2[k].quantity
                ) {
                  return false;
                }
              }
            }
          }
        }
      } else {
        // Compare items in modifier_group
        if (obj1.modifier_group.length !== obj2.modifier_group.length) {
          return false;
        }

        for (let i = 0; i < obj1.modifier_group.length; i++) {
          const group1 = obj1.modifier_group[i];
          const group2 = obj2.modifier_group[i];
          if (group1.items.length !== group2.items.length) {
            return false;
          }

          for (let j = 0; j < group1.items.length; j++) {
            if (
              group1.items[j].id !== group2.items[j].id ||
              group1.items[j].quantity !== group2.items[j].quantity
            ) {
              return false;
            }
          }
        }
      }

      // All checks passed
      return true;
    },
  },
  watch: {
    reloadModal() {
      setTimeout(() => {
        this.itemModifierKey = Math.random();
      }, 100);
      this.choiceSelected = {};
      this.selectedChoiceIndex = 0;

      if (this.itemId) {
        // A new photo fades in once loaded (same photo: its <img> is kept).
        if (this.itemDetail.image !== this.itemInfo.image) this.heroLoaded = false;
        // Deep clone item data that has been restored modifiers by Checkout
        this.itemInfo = JSON.parse(JSON.stringify(this.itemDetail));
        // KSK054: item detail (choices, modifier groups/options) is fetched per
        // item, so its names are not in the menu's translation batch.
        syncMenuTranslations(collectNamesDeep(this.itemInfo));

        // Kiosk: backend auto-selects first item in required (min=1, max=1) groups
        // and bakes its price into price_default. Undo it for new-add so user picks.
        // Skip when editing from cart — preserve user's saved selections.
        if (!this.editMode) {
          this.disableAutoSelectFromAPI();
        }

        if (this.itemInfo.choice) {
          // If editing in cart (already has choice_selected from Checkout)
          if (this.itemInfo.choice_selected && this.itemInfo.choice_selected.items) {
            // Find which choice index was previously selected by matching choice id
            let restoredIndex = 0;
            if (this.itemInfo.choices && this.itemInfo.choice_selected.id) {
              const foundIndex = this.itemInfo.choices.findIndex(
                (c) => c.id === this.itemInfo.choice_selected.id
              );
              if (foundIndex >= 0) restoredIndex = foundIndex;
            }

            // Use choices from API (already has modifier states restored by Checkout.restoreModifiersFromCart)
            // Do NOT replace with choice_selected — it only has selected modifiers, not the full list
            this.originalChoices = JSON.stringify(this.itemInfo.choices);

            this.selectedChoiceIndex = restoredIndex;
            this.choiceSelected = JSON.parse(JSON.stringify(this.itemInfo.choices[restoredIndex]));

            if (this.itemInfo.is_sub_category) {
              // Restore which single item was selected, then derive price from it.
              const savedItems = this.itemInfo.choice_selected.items || [];
              const savedId = savedItems.length ? savedItems[0].id : null;
              let selIdx = this.choiceSelected.items.findIndex(
                (it) => it.id === savedId
              );
              if (selIdx === -1) selIdx = 0;
              this.selectedSubCategoryIndex = selIdx;
              this.recalcSubCategoryPrice();
            } else {
              // Set base price from choice, then add selected modifier prices
              this.itemInfo.price_default_no_modifier_option = this.choiceSelected.default_price;
              this.itemInfo.price_default = parseFloat(this.choiceSelected.default_price);
              this.itemInfo.member_price_no_modifier_option = this.choiceSelected.member_price;
              this.itemInfo.member_price = parseFloat(this.choiceSelected.member_price);
              this.applyChoiceSchedule();
              this.recalcChoiceModifierPrice();

              // Highlight the correct tab after DOM renders
              this.$nextTick(() => {
                $(".choice-list > .choice-tab").removeClass("active");
                $(".choice-list > .choice-" + restoredIndex).addClass("active");
              });
            }
          } else {
            // If adding new from menu, use default selection as before
            this.originalChoices = JSON.stringify(this.itemInfo.choices);
            this.selectChoice(0);
          }
        } else {
          this.originalChoices = JSON.stringify(this.itemInfo.choices);
        }

        if (this.itemInfo.slides) {
          var usedItems = [];
          this.itemInfo.slides.forEach((slide) => {
            if (slide.direct_to_suggested_adds_on === 1) {
              this.itemInfo.modifier_group.forEach((modifierGroup) => {
                const selectedItem = modifierGroup.items.find(
                  (item) => item.price > 0 && !usedItems.includes(item.id)
                );
                if (selectedItem) {
                  usedItems.push(selectedItem.id);
                  slide.itemSelected = selectedItem;
                } else {
                  const selectedItemPriceIsZero = modifierGroup.items.find(
                    (item) => item.price == 0 && !usedItems.includes(item.id)
                  );
                  if (selectedItemPriceIsZero) {
                    usedItems.push(selectedItemPriceIsZero.id);
                    slide.itemSelected = selectedItemPriceIsZero;
                  }
                }
              });
            }
          });
        }

        this.itemInfo.price_default = parseFloat(this.itemInfo.price_default);
        this.itemInfo.member_price = parseFloat(this.itemInfo.member_price);

        this.checkAvailableAddToCart();

        this.isLoadDone = true;
        this.resetSheetUi();
      } else {
        this.isLoadDone = false;
      }
    },
  },
};
</script>

<style scoped>
/* KSK021 — P4 Item Detail (customize) sheet, kiosk theme 2. */

/* Sheet: fills the whole screen (header included), so the item gets the
   full height. Spacing is tighter than the menu to fit more options. */
.ksk-sheet.modal {
  --ksk-sheet-pad: var(--ksk-space-3);
  --ksk-sheet-bar: 128px;
  top: 0;
  height: 100%;
  overflow: hidden;
}
.ksk-sheet .modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
}
/* KSK063: open grows out from the centre with a decelerating curve.
   KSK071: close is a short plain fade — no shrink, and the blurred hero
   backdrop is dropped first — because scaling the blur lagged on the kiosk.
   (Each state's transition applies when entering it.) */
.ksk-sheet.fade {
  transition: opacity 0.15s ease-in;
}
.ksk-sheet.fade.show {
  transition: opacity 0.28s ease-out;
}
.ksk-sheet.fade .modal-dialog {
  transform: none;
  scale: 0.92;
  transition: scale 0.2s ease-in;
  will-change: scale;
}
.ksk-sheet.fade.show .modal-dialog {
  scale: 1;
  transition: scale 0.36s cubic-bezier(0.22, 1, 0.36, 1);
}
.ksk-sheet.fade .modal-dialog.is-closing {
  scale: 1;
  transition: none;
}
.ksk-sheet .modal-dialog.is-closing .ksk-hero__backdrop {
  visibility: hidden;
}
@media (prefers-reduced-motion: reduce) {
  .ksk-sheet.fade,
  .ksk-sheet.fade .modal-dialog {
    transition: none;
  }
}
.ksk-sheet .modal-content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: 0;
  /* KSK108: room for the sheet's fixed Powered-by strip. */
  padding-bottom: var(--ksk-powered-height);
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
  overflow: hidden;
}
.ksk-sheet__body {
  flex: 1;
  min-height: 0;
  padding: 0 !important;
  overflow-y: auto;
  overflow-x: hidden;
}
.ksk-sheet__content {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
  padding: 0 var(--ksk-sheet-pad) var(--ksk-space-4);
}

/* KSK063: loading placeholder. Pulses opacity only (compositor-friendly). */
.ksk-skel__hero {
  background: var(--ksk-surface-muted);
}
.ksk-skel__card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
  padding: var(--ksk-space-4);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-float);
}
.ksk-skel__line {
  height: 20px;
  width: 100%;
  border-radius: 10px;
  background: var(--ksk-surface-muted);
  animation: ksk-skel-pulse 1.1s ease-in-out infinite alternate;
}
.ksk-skel__line--title {
  height: 36px;
  width: 60%;
}
.ksk-skel__line--price {
  height: 28px;
  width: 28%;
}
.ksk-skel__line--short {
  width: 45%;
}
.ksk-skel__line--row {
  height: 64px;
  border-radius: var(--ksk-radius-button);
}
@keyframes ksk-skel-pulse {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.45;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ksk-skel__line {
    animation: none;
  }
}

/* Close buttons */
.ksk-sheet__close {
  width: 56px;
  height: 56px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ksk-border);
  border-radius: 50%;
  background: var(--ksk-surface);
  color: var(--ksk-text);
  box-shadow: var(--ksk-shadow-float);
  cursor: pointer;
  transition: transform 80ms ease-out;
}
.ksk-sheet__close:active {
  transform: scale(0.94);
}
.ksk-sheet__close .material-icons-round {
  font-size: 30px;
}
.ksk-sheet__close--hero {
  position: absolute;
  top: var(--ksk-space-3);
  right: var(--ksk-space-3);
  z-index: 1;
}

/* Sticky title bar */
.ksk-sheet__title-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  height: 80px;
  display: flex;
  align-items: center;
  gap: var(--ksk-space-3);
  padding: 0 var(--ksk-sheet-pad);
  background: var(--ksk-surface);
  border-bottom: 1px solid var(--ksk-border);
  box-shadow: var(--ksk-shadow-card);
  opacity: 0;
  transform: translateY(-100%);
  pointer-events: none;
  transition: opacity 0.2s, transform 0.2s;
}
.ksk-sheet__title-bar.is-visible {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}
.ksk-sheet__title-name {
  flex: 1;
  min-width: 0;
  font-size: 26px;
  line-height: 34px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ksk-sheet__title-price {
  flex: none;
  padding: 4px 16px;
  font-size: 26px;
  font-weight: 700;
  color: var(--ksk-on-primary);
  font-variant-numeric: tabular-nums;
  /* KSK081: theme pill; text black/white by theme lightness (--ksk-on-primary). */
  background: var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
}
.ksk-sheet__close--bar {
  box-shadow: none;
  background: var(--ksk-surface-muted);
  border-color: transparent;
}

/* Hero: edge to edge at the top of the sheet; the info card overlaps its
   bottom edge (--ksk-hero-overlap). Photos are square, so the photo is shown
   whole in a centred frame above the overlap, and a blurred, enlarged copy
   fills the full width behind it. */
.ksk-hero {
  --ksk-hero-overlap: 40px;
  position: relative;
  flex: none;
  height: 608px;
  margin: 0 calc(-1 * var(--ksk-sheet-pad)) calc(-1 * var(--ksk-hero-overlap));
  overflow: hidden;
  background: var(--ksk-surface-muted);
}
.ksk-hero__backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(28px) saturate(1.2);
  transform: scale(1.2);
  opacity: 0;
  transition: opacity 0.4s ease-out;
}
.ksk-hero__backdrop.is-loaded {
  opacity: 0.85;
}
.ksk-hero__frame {
  position: absolute;
  top: var(--ksk-space-3);
  bottom: calc(var(--ksk-hero-overlap) + var(--ksk-space-2));
  left: var(--ksk-space-3);
  right: var(--ksk-space-3);
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Square photo: full frame height. A wider/taller photo still fits whole. */
.ksk-hero .food-image {
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  border-radius: var(--ksk-radius-card);
  box-shadow: 0 12px 32px rgba(20, 26, 20, 0.25);
  opacity: 0;
  transform: scale(0.96);
  transition: opacity 0.3s ease-out, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.ksk-hero .food-image.is-loaded {
  opacity: 1;
  transform: none;
}

/* Cards */
.ksk-card {
  padding: var(--ksk-space-3);
  border: 1px solid var(--ksk-border);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
}

/* Info: sits above the hero's bottom edge. */
.ksk-info {
  position: relative;
  z-index: 1;
  box-shadow: var(--ksk-shadow-float);
}
.ksk-info__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ksk-space-3);
}
.ksk-info__name {
  margin: 0;
  font-family: var(--ksk-font);
  font-size: 34px;
  line-height: 42px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ksk-text);
  overflow-wrap: anywhere;
}
.ksk-info__prices {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.ksk-info__price-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ksk-info__price {
  padding: 0 18px;
  font-size: 32px;
  line-height: 42px;
  font-weight: 700;
  color: var(--ksk-on-primary);
  font-variant-numeric: tabular-nums;
  /* KSK081: theme pill; text black/white by theme lightness (--ksk-on-primary). */
  background: var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
}
.ksk-info__regular {
  font-size: 22px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
  text-decoration: line-through;
}
.ksk-info__member {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
}
.ksk-info__member .material-icons-round {
  font-size: 20px;
}
.ksk-info__desc {
  margin: var(--ksk-space-1) 0 0;
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
  white-space: pre-line;
}
.ksk-info__desc.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ksk-info__more {
  margin-top: 4px;
  padding: 8px 0;
  border: none;
  background: none;
  color: var(--ksk-primary);
  font-family: var(--ksk-font);
  font-size: var(--ksk-body-size);
  font-weight: 700;
  cursor: pointer;
}
.ksk-info__bundle {
  margin: var(--ksk-space-3) 0 0;
  padding: var(--ksk-space-2) 0 0;
  list-style: none;
  border-top: 1px solid var(--ksk-border);
}
.ksk-info__bundle li {
  display: flex;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  padding: 6px 0;
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
}
.ksk-info__bundle-desc {
  display: block;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-info__bundle-qty {
  flex: none;
  font-weight: 700;
}

/* Add-on carousel */
.ksk-addons {
  position: relative;
  border-radius: var(--ksk-radius-card);
  overflow: hidden;
  box-shadow: var(--ksk-shadow-card);
}
.ksk-addons__slide {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ksk-addons__bg {
  display: block;
  width: 100%;
}
.ksk-addons__card {
  position: absolute;
  left: var(--ksk-space-3);
  right: var(--ksk-space-3);
  bottom: var(--ksk-space-3);
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  padding: var(--ksk-space-2);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--ksk-shadow-float);
}
.ksk-addons__card img {
  width: 96px !important;
  max-width: 96px;
  height: 96px;
  flex: none;
  border-radius: 14px;
  object-fit: cover;
}
.ksk-addons__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.ksk-addons__tag {
  align-self: flex-start;
  padding: 2px 12px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
  font-size: 18px;
  font-weight: 700;
}
.ksk-addons__name {
  font-size: var(--ksk-item-size);
  line-height: var(--ksk-item-lh);
  font-weight: 600;
  color: var(--ksk-text);
}
.ksk-addons :deep(.swiper-pagination-bullet-active) {
  background: var(--ksk-primary);
}

/* Set-meal choices — labels wrap, never truncate */
.ksk-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ksk-space-2);
}
.ksk-tabs .choice-tab {
  flex: 1 1 220px;
  min-height: 104px;
  padding: 20px var(--ksk-space-3);
  border: 2px solid var(--ksk-border);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  color: var(--ksk-text);
  font-family: var(--ksk-font);
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: transform 80ms ease-out;
}
.ksk-tabs .choice-tab:active {
  transform: scale(0.97);
}
.ksk-tabs .choice-tab.active {
  border-color: var(--ksk-primary);
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
}

/* Items inside a set-meal choice */
.ksk-choice-item {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
}
.ksk-choice-item__head {
  display: flex;
  flex-direction: column;
  padding: var(--ksk-space-1) var(--ksk-space-1) 0;
}
.ksk-choice-item__name {
  font-size: 26px;
  line-height: 34px;
  font-weight: 700;
}
.ksk-choice-item__desc {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}

/* Sub-category radio rows */
.ksk-subcat {
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-2);
  scroll-margin-top: 120px;
}
.ksk-subcat__row {
  padding: 0 var(--ksk-space-3);
  border: 3px solid transparent;
  border-radius: 22px;
  background: var(--ksk-background);
  cursor: pointer;
  scroll-margin-top: 120px;
  transition: transform 80ms ease-out;
}
.ksk-subcat__row:active {
  transform: scale(0.99);
}
.ksk-subcat__row.is-selected {
  border-color: var(--ksk-primary);
  background: var(--ksk-primary-container);
}
.ksk-subcat__row.is-sold-out {
  opacity: 0.4;
  cursor: not-allowed;
}
.ksk-subcat__main {
  min-height: 80px;
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  padding: var(--ksk-space-2) 0;
}
.ksk-subcat__radio {
  flex: none;
  width: 36px;
  height: 36px;
  border: 2px solid var(--ksk-border);
  border-radius: 50%;
  background: var(--ksk-surface);
}
.is-selected .ksk-subcat__radio {
  border: 10px solid var(--ksk-primary);
}
.ksk-subcat__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ksk-subcat__name {
  font-size: var(--ksk-item-size);
  line-height: var(--ksk-item-lh);
  font-weight: 600;
}
.is-selected .ksk-subcat__name {
  font-weight: 700;
  color: var(--ksk-primary);
}
.ksk-subcat__desc {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-subcat__sold-out {
  align-self: flex-start;
  padding: 2px 12px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-error);
  color: var(--ksk-on-status);
  font-size: 16px;
  font-weight: 700;
}
.ksk-subcat__price {
  flex: none;
  font-size: var(--ksk-price-size);
  font-weight: 700;
  color: var(--ksk-primary);
  font-variant-numeric: tabular-nums;
}
.ksk-subcat__summary {
  margin: 0 0 var(--ksk-space-2) 52px;
  padding-top: 12px;
  border-top: 1px dashed var(--ksk-border);
}
.ksk-subcat__mod {
  display: flex;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  padding: 2px 0;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-subcat__mod.nested {
  padding-left: var(--ksk-space-3);
}
.ksk-subcat__mod-price {
  white-space: nowrap;
  color: var(--ksk-primary);
  font-weight: 600;
}

/* Unmet required message */
.ksk-sheet__unmet {
  position: absolute;
  left: var(--ksk-sheet-pad);
  right: var(--ksk-sheet-pad);
  bottom: calc(var(--ksk-sheet-bar) + var(--ksk-powered-height) + var(--ksk-space-2));
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: var(--ksk-space-2) var(--ksk-space-3);
  border-radius: 20px;
  background: var(--ksk-error);
  color: var(--ksk-on-status);
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  font-weight: 600;
  box-shadow: var(--ksk-shadow-float);
}
.ksk-sheet__unmet .material-icons-round {
  font-size: 32px;
}
.ksk-fade-enter-active,
.ksk-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.ksk-fade-enter-from,
.ksk-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* Bottom bar */
.ksk-sheet__bar {
  flex: none;
  height: var(--ksk-sheet-bar);
  display: flex;
  align-items: center;
  gap: var(--ksk-space-3);
  padding: 0 var(--ksk-sheet-pad);
  background: var(--ksk-surface);
  border-top: 1px solid var(--ksk-border);
  box-shadow: var(--ksk-shadow-bar);
  z-index: 3;
}
.ksk-sheet__bar .btn-add-to-cart {
  flex: 1;
  font-size: 24px;
  font-weight: 700;
}
.ksk-sheet__bar .btn-add-to-cart.is-incomplete {
  background: var(--ksk-surface-muted);
  color: var(--ksk-text-secondary);
  box-shadow: none;
}

/* Modifier loading */
.modifier-loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-2);
  min-height: 120px;
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  color: var(--ksk-text-secondary);
}
.modifier-loading-container .spinner-border {
  color: var(--ksk-primary);
}
.loading-text {
  font-size: var(--ksk-body-size);
}

</style>
